/**
 * <ListView> — the platform's advanced list/grid, as a reusable, view-driven
 * component (the "one library used across the app like Kendo" thesis, on the LIST
 * axis). It carries the machinery the CRM pipeline pioneered — persona-resolved
 * saved views (core.view) with view tabs · fork-never-mutate · filter chips ·
 * column ⋮ menu (sort/filter/hide/add) · inline cell editing · selection + batch
 * toolbar · governed create · ⌘K scope · MCP reveal · row-click open — driven by
 * a per-surface ListConfig, so CRM, AFD, and every module render the SAME grid.
 *
 * Entity-specific bits (custom cell renderers, cell menus, quick-create, peek,
 * batch actions, column-menu filter presets) are injected via config; the generic
 * view/filter/column/selection/create/⌘K machinery lives here once.
 *
 * ── the grid itself is `@solum/ui`'s <DataGrid> (#959) ─────────────────────
 * The tabular half — `role="grid"`/`treegrid`, the column headers, the roving
 * tabindex, the inline-edit keyboard model, the selection tile, the expansion
 * row — moved to the shared control kit, unchanged, so that
 * `apps/portal` can render the SAME surface. An app cannot import from another
 * app, and that single fact is why a customer administrator got a `<ul>` of
 * cards where an internal user got this.
 *
 * What stayed here is everything the grid must not know: persona-resolved
 * saved views and their fork/pin cascade, the filter chips, the ⌘K scope, the
 * crumb bar, the split pane and the peek, the MCP reveal, and every query.
 * ListView is the console's PAGE; DataGrid is the grid. The console's behaviour
 * is unchanged by construction — it renders the same component it used to
 * inline.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react'
import type { Row } from '@solum/sdk'
import {
  Button, Spinner, cx, Badge, CollectionWorkspace,
  DataGrid, GridPager, Calendar, Board, EntityIcon, Modal,
  ColumnFilterEditor, summarizeColumnFilter, WorkspaceSplit, useTheme, PageShell, Select, TablePresentationComposer, Notice,
  PeekHeader, PeekBody, AnchoredSurface, SavedViewSelector, Menu, optionLabel, type SavedViewScope,
  type GridColumn, type GridCellCtx, type ColumnFilterLeaf, type CalendarEvent, type BoardItem,
} from '@solum/ui'
import {
  GridColumnMenu, GridFilterPills, GridSearch, ViewSettingsMenu, describeColumnFilter, togglePinnedColumn,
  type GridFilterPill, type GridGrouping,
} from '@solum/ui'
import { useCanonicalForm, useEntityRows, useEntityPage, useEntityRow, useSemantics, useSaveEntity, useSchema, useEntityIdentities, useRefLabels, useReferenceTargets, useEntityStatusMachine, usePickUpWork, useRecordIdentities, useTablePresentation, useRequestedTablePresentation, useIsSubjectEntity, useGroupedEntityPage, PAGE_SIZE, SUBJECT_PAGE_SIZE, type StatusMachine } from '../data/queries'
import {
  booleanGroupDomain, canonicalCommandBar, cardColumnRoles, groupKeyOf, groupLabelOf, groupRowsInMemory,
  cappedClientCollection, matchesCollectionText, optionGroupDomain, resolveCollectionGrouping,
  resolveTableCommands, serverPagedCollection, type GroupDomain,
} from '@solum/experience'
import { resolveColumnFilterField, kindResolver, replaceColumnFilters, subjectFilterField } from './columnFilterField'
import { RefPicker } from '../form/RefPicker'
import {
  combineOrGroups, encodeSortSpec, filtersToPostgrest, leavesToSubjectFilters, parseSortSpec, searchCondition,
  sortToPostgrest, sortToSubject,
} from '@solum/sdk'
import { riskMeasures, rowFlags } from '../data/highlights'
import {
  usePersona, useViews, useRegistryExperience,
  applyView, useForkView, usePinView, me, type Leaf,
} from '../experience/governed'
import { recordRecent } from '../app/recents'
import { humanizeKey, pluralize, singularize } from '@solum/experience'
import { openPalette, setPaletteScope, type PaletteScope } from '../app/paletteScope'
import { setDrawerContext } from '@solum/ui'
import { useRelatedPresentation } from '../record/useRelatedPresentation'
import { formLayout } from '../record/formLayout'
import { buildForm } from '@solum/experience'
import type { TableSlotChoice } from '@solum/ui'
// Shared grid styling lives in the CRM stylesheet (the pipeline pioneered it);
// ListView reuses it so every surface is pixel-identical. (A later cosmetic move
// to list/list.module.css is a pure rename — the class NAMES are the contract.)
import lv from './listView.module.css'
import { QueryBoundary, queryState, type QueryLike } from '../data/QueryBoundary'
import { confirmedWrite, writeDoorOf } from '../data/writeState'
import { Link } from '@tanstack/react-router'
import { useListActionSeam } from './listActionSeam'
import { RelationBand } from '../record/RelationBand'
import { RecordBody } from '../record/RecordBody'
import { setTrail } from '../record/trail'
import { listSlots } from '../record/relations'
import { useColumnWidths } from './useColumnWidths'
import { useCollectionCommands } from '../record/EntityActions'
import { canonicalGridUrl, decodeFilterParam, decodeSortParam, encodeSortParam, sameGridUrl, useGridUrl } from './gridUrlState'
import { effectiveCommandBar, useGridPresentation } from './useGridPresentation'
import { useSplitWidth } from './useSplitWidth'
import { movePage, pageOffset, type PageCursor } from './pageCursor'
import { cycleSortTerm, reorderColumns, setSortTerm } from './viewFluency'
import { OUTCOME_CATEGORY } from '../lifecycle/statusMachine'
import {
  targetOwnsTablePresentation,
  tablePresentationTargetKey,
  tablePresentationWriteCallbacks,
  useClearTablePresentation,
  useSetTablePresentation,
  useResolvedQuickCreateForm,
  useTablePresentationAuthority,
  useTablePresentationCatalogues,
  useTablePresentationSettings,
  type TablePresentationHost,
} from './useTablePresentationSettings'
import { GovernedQuickCreate } from './GovernedQuickCreate'
import { WriteFailureNotice } from '../data/WriteFailure'

export type { Leaf }
/**
 * `kind`/`group_by`/`date_col` are the RENDER AXIS (core 2026081005): the same
 * saved view drawn as a grid, a board (grouped by an enumerable field) or a
 * calendar (placed on a date field). They are carried here — and through the
 * fork in `useForkView` — deliberately ahead of the renderers, because a field
 * the client drops is how `group_by` stayed write-only for 155 migrations.
 * ALL THREE kinds are wired to `@solum/ui`: `<Calendar>` and `<Board>` below.
 * This paragraph used to say board was unwired and drew as the grid — measured
 * 2026-08-30 against the code in this same file, which renders
 * `<Board items={boardItems}>`. A stale comment about a renderer is worse than
 * none: #1649 read it as "the board lens does not work yet" and came close to
 * growing a bid-desk board of its own (skill rule 43).
 */
/**
 * #1982: the lens vocabulary is DATA, not this union. `core.view_kind` registers
 * the legal kinds (`grid`, `board`, `calendar` and, since core 0.338.0, `cards`)
 * and the entity's own declaration says which of them it OFFERS.
 *
 * This type stays as the shape of a saved view's `kind` column — a saved view
 * still selects exactly one lens, and its shape validation is unchanged. What it
 * no longer decides is AVAILABILITY: that comes from
 * `useTablePresentation(entity)`, and a saved view naming a lens the entity does
 * not offer is reported as a diagnostic and drawn as a grid, never as a blank
 * surface.
 */
/* This host's renderer inventory (#1982). `cards` joined it in the closure
   slice: the comment below already claimed the console "draws all four" while
   this list held three, so an entity offering cards resolved to the grid with
   no diagnostic — a silent substitution, which is the one thing the named
   fallback exists to prevent. It is not a fourth row renderer: the cards lens
   is <DataGrid presentation="cards">, whose recipe is derived from the governed
   card spec rather than typed into a page. */
export const HOST_LENSES = ['grid', 'board', 'calendar', 'cards'] as const
export const PORTAL_HOST_LENSES = ['grid', 'cards'] as const

/** The field kinds a grid SEARCH asks with `ilike` (#2375): text a person typed. */
const SEARCHABLE_KINDS: ReadonlySet<string> = new Set(['text', 'email', 'phone'])
/** The view-settings ⋮ "Display" radio, per lens this host draws (#2375 §3.6). */
const LENS_SETTING_LABEL: Record<ViewKind, string> = {
  grid: 'Show as table', cards: 'Show as cards', board: 'Show as board', calendar: 'Show as calendar',
}
/** The neutral domain handed to the grouped read while it is disabled. */
const EMPTY_GROUP_DOMAIN: GroupDomain = { column: 'id', values: [], textual: false }

export type ViewKind = 'grid' | 'board' | 'calendar' | 'cards'
export type ViewDoc = {
  id: string; name: string; scope: string; persona_key: string | null
  columns: string[]; filters: Leaf[]; sort_spec: string[]; is_default?: boolean
  kind?: ViewKind; group_by?: string | null; date_col?: string | null
}
const asView = (r: Row): ViewDoc => ({
  id: String(r.id), name: String(r.name), scope: String(r.scope), persona_key: (r.persona_key as string) ?? null,
  columns: (r.columns as string[]) ?? [], filters: (r.filters as Leaf[]) ?? [], sort_spec: (r.sort_spec as string[]) ?? [],
  is_default: Boolean(r.is_default),
  kind: ((r.kind as ViewKind) ?? 'grid'), group_by: (r.group_by as string) ?? null, date_col: (r.date_col as string) ?? null,
})

/** Queue assignment is DATA on an opted-in work host. The server remains the
 * authority: this predicate only decides whether to OFFER the existing verb;
 * `core.pick_up_work` rechecks registration, pool membership, row authority and
 * the conditional-update race. */
export const isQueuePickupRow = (row: Row): boolean =>
  row.assignee_kind === 'work_queue' && row.assignee_id != null && row.picked_up_by == null

/** Preserve the kernel's named race outcome as something a person can act on.
 * Other refusals remain verbatim: hiding an authority or membership refusal
 * behind a generic toast would turn a defensible server answer into guesswork. */
export function pickupRefusal(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error ?? 'Pick up was refused.')
  return /already taken/i.test(message)
    ? 'Already taken — another queue member picked up this item first.'
    : message
}

/** Context handed to a column's custom cell renderer. */
export interface CellCtx {
  editing: boolean
  startEdit: () => void
  /** Returns the write when there is one, so a governed menu can wait (#2149). */
  commit: (value: unknown) => void | Promise<unknown>
  openMenu: (anchor: HTMLElement) => void
  flashing: boolean
  /**
   * The governed title of a reference value in THIS column, resolved by id.
   *
   * Since #1993 the column's TARGET is derived from the registered relationship
   * metadata, so this is populated for every declared reference — not only the
   * columns a page happens to name in `ListConfig.refs` — and the label comes
   * from `core.titles_of` under the caller's own RLS rather than from a
   * client-side column-name guess. A target that is missing, deleted or not
   * visible to this session is indistinguishable by design, so the fallback is
   * an ordinary case; it must never be the id.
   */
  refLabel: (id: unknown, fallback?: string) => string
  /**
   * The same, for a reference column OTHER than the one being rendered.
   *
   * A column does not have to display its own value: Subscriptions renders
   * `plan_id`'s name inside the `quantity` cell ("Pro × 8"). Keying the resolver
   * on the cell's column silently returned the fallback there — the plan name
   * rendered as "—" and only the browser suite caught it.
   */
  refLabelOf: (column: string, id: unknown, fallback?: string) => string
}
export interface ListColumn {
  width?: string
  /** custom cell body; omit for the generic text renderer */
  cell?: (row: Row, ctx: CellCtx) => ReactNode
  editable?: boolean
  editKind?: 'text' | 'number' | 'date'
  /** a click-to-open cell menu (e.g. a tone picker / relation picker) */
  menu?: (row: Row, anchor: HTMLElement, close: () => void, commit: (v: unknown) => void | Promise<unknown>) => ReactNode
  /** column ⋮ filter presets for this column */
  colMenuFilters?: { label: string; apply: (v: ViewDoc) => void }[]
  align?: 'left' | 'right'
  /** Role in the opt-in compact card presentation. */
  compact?: 'primary' | 'detail' | 'actions' | 'hidden'
  /** What this column's text is — the row's name, or a supporting fact. */
  emphasis?: 'name' | 'supporting'
}
export interface BatchAction {
  label: string
  danger?: boolean
  run?: (ids: string[]) => void
  menu?: (anchor: HTMLElement, ids: string[], close: () => void) => ReactNode
}
export interface ListConfig {
  entity: string
  /** route base for peek navigation, e.g. '/crm' or '/delivery/gates' */
  routePath: string
  /**
   * A PRESENTATION OVERRIDE for reference columns: `{ account_id:
   * 'core_organization' }`.
   *
   * No longer required for correctness (#1993): ListView derives every declared
   * reference target from `core.describe_reference_targets` and resolves the
   * labels for exactly the ids ON THE CURRENT PAGE through `core.titles_of`.
   * A map here still WINS, for a desk that means something different by a
   * column than the relationship registry does.
   *
   * Every desk used to fetch a 500-row pool of the target entity and `.find()`
   * in it, which renders the wrong NAME (not a short list) once the target
   * table passes 500 rows (TEC-02).
   */
  refs?: Record<string, string>
  /** singular label; derived from the schema if omitted */
  label?: string
  eyebrow?: string
  title?: string
  columns: Record<string, ListColumn>
  addableColumns?: string[]
  widths?: Record<string, string>
  chipLabel?: (f: Leaf) => string
  /** the peek panel body for a row (rendered in an <aside>); omit for no peek */
  peek?: (row: Row, ctx: { orderedIds: string[]; navigate: (id: string) => void; close: () => void }) => ReactNode
  batchActions?: BatchAction[]
  /** Create through a surface-owned flow (for example a modal or full form). */
  onNew?: () => void
  mcpToolName?: string
  /**
   * Inline relationship bands — expanding a row shows what it is related to,
   * with a chip per registered slot and a count on each.
   *
   * ON by default, and safe to be: a twist only appears where the metamodel has
   * registered at least one list slot for the entity, so a surface whose rows
   * have no relationships is untouched. Set false where the row's own context is
   * the whole point and a band would be a distraction.
   */
  relatedBands?: boolean
  /**
   * This surface cannot create records directly — hide the affordances that
   * would 403 (#877).
   *
   * Set by the GENERIC browser from the entity's registered `write_door`: an
   * `rpc` door means `core.enforce_write_doors()` has revoked insert/update/
   * delete for every authenticated role, so ＋New leads to a form whose Save
   * cannot work. Not a permission the
   * current user lacks — a door that is shut for everybody.
   *
   * Bespoke desks leave it unset: they route their writes through the module's
   * governed RPCs (CommerceDesk → `advance_order` / `request_full_return`),
   * which is exactly what the door exists to force.
   */
  readOnly?: boolean
  /** Said next to the missing ＋New, so its absence is explained rather than odd. */
  readOnlyReason?: string
  /** Legacy responsive cards for a page without a governed Cards lens. */
  compact?: 'cards'
}

interface ListCreatePaletteInput {
  readOnly: boolean
  navigationEligible: boolean
  governedDeclared: boolean
  governedReady: boolean
  singularLabel: string
  onGovernedCreate: () => void
  onNew?: () => void
}

/** One decision table for every palette registration/re-registration. Keeping
 * this outside the effect makes the loading-to-ready transition directly
 * testable: an unresolved declaration exposes no legacy bypass, and the same
 * scope gains only the governed verb once its form resolves. */
export function listCreatePalette(input: ListCreatePaletteInput): Pick<PaletteScope, 'create' | 'verbs'> {
  if (input.readOnly || !input.navigationEligible) return { verbs: [] }
  const label = input.singularLabel.toLowerCase()
  /* ONE create, and the palette runs the SAME action the toolbar does (#2406
     ruling 79217). The table used to have four outcomes and a second verb:
     a governed form, a surface's own flow, a ghost row, and `Quick add …`
     which armed the ghost beside a real form. Three of those are gone with the
     mechanism, and with them the "duplicated create decision table" the ruling
     names.

     A DECLARED-BUT-UNRESOLVED form still yields NOTHING, which is the one arm
     worth keeping deliberately: falling through would offer a command that
     bypasses the governed field subset during exactly the refusal or slow read
     that makes it matter. The ruling says the same thing from the other side —
     "if no governed create form/action resolves, the New affordance is absent
     or carries the existing explicit refusal. It never falls back to a ghost
     row or a no-op." */
  if (input.governedDeclared) {
    return input.governedReady
      ? { create: { label: `New ${label}`, run: input.onGovernedCreate }, verbs: [] }
      : { verbs: [] }
  }
  return input.onNew
    ? { create: { label: `New ${label}`, run: input.onNew }, verbs: [] }
    : { verbs: [] }
}

const defaultChipLabel = (f: Leaf): string => `${f.field} ${f.op} ${String(f.value)}`

// AI Data Highlight (#10): surface at-risk rows. A row is "overdue" if a
// due/deadline/expiry date is in the past and it isn't in a DONE state.
const OVERDUE_KEY = /(due|deadline|expire)/i

/**
 * Which states count as finished — asked of the entity's own machine (#1674).
 *
 * ── the defect ─────────────────────────────────────────────────────────────
 * This was `DONE_STATUS`, a set of eleven words, and it was a hand-written copy
 * of a column: `core.status_state.category`, which `core 0046` added for
 * exactly this question. Measured across every `register_status_machine` /
 * `register_status_options` payload in `modules/` and `packages/core`:
 *
 *   · 118 distinct state codes carry a category; 44 of them are `done`, and
 *     **33 of those 44 were not in the eleven**. A `commerce_return` in
 *     `refunded` — `{"category":"done","is_locked":true}` — with any past
 *     `due`/`deadline`/`expire` column was painted warn "overdue" on the very
 *     list `ReturnsDesk` renders. So were `fulfilled`, `granted`, `returned`,
 *     `terminated`, `withdrawn`, `met`, `no_bid` and twenty-six more.
 *   · and it was wrong in the OTHER direction too: `paid`, `resolved` and
 *     `published` are each `category:"open"` on some entity, so three of the
 *     eleven words suppressed the flag on rows that really were in flight.
 *
 * ── why lengthening the list was not the fix ───────────────────────────────
 * Twenty-six codes carry more than one category across the fleet. `submitted`
 * is `done` for a `tenders_response` and `open` for a `tenders_tender` and a
 * `controlplane_release`. The same word, finished on one entity and in flight
 * on two others — so the question only has an answer PER ENTITY, and a global
 * set is the wrong shape, not merely the wrong contents.
 *
 * ── what "finished" means, measured rather than assumed ────────────────────
 * The seeds use ten categories, not two: open 177 · done 120 · closed 24 ·
 * live 7 · refused 6 · active 5 · draft 4 · clear 4 · blocked 4 · admitted 2.
 * `done`, `closed` and `refused` are the three that name an OUTCOME; the rest
 * name a stage. Rather than judge the remaining seven, a state the machine
 * declares no way OUT of is also finished — derived, so a lifecycle that grows
 * an eleventh category needs no edit here. That clause is skipped when the
 * machine declares no transitions at all, which would otherwise make every
 * state terminal.
 *
 * The eleven words survive as the fallback, and only as that: they are the
 * answer for the entities with no registered machine (billing, cpq,
 * subscriptions, time and funding register none), where the vocabulary is the
 * only source there is.
 */
const DONE_WORDS = new Set(['done', 'closed', 'paid', 'resolved', 'cancelled', 'won', 'lost', 'published', 'archived', 'complete', 'completed'])
export function isDone(status: string, machine: StatusMachine | null): boolean {
  const states = machine?.states
  const st = states?.length ? states.find((x) => x.code === status) : undefined
  // A code the machine does not declare is not silently "not done": fall
  // through to the vocabulary rather than treat an unknown as in-flight.
  if (!st) return DONE_WORDS.has(status)
  if (OUTCOME_CATEGORY.has(st.category)) return true
  const edges = machine?.transitions ?? []
  return edges.length > 0 && !edges.some((t) => t.from === status)
}
function rowTone(d: Row, machine: StatusMachine | null): 'warn' | undefined {
  if (isDone(String(d.status ?? '').toLowerCase(), machine)) return undefined
  for (const [k, v] of Object.entries(d)) {
    if (v && OVERDUE_KEY.test(k)) { const t = Date.parse(String(v)); if (!Number.isNaN(t) && t < Date.now()) return 'warn' }
  }
  return undefined
}

export interface ListViewProps {
  config: ListConfig
  /** the open peek id (from the route search), and a navigator for it */
  peekId?: string
  onPeek: (id: string | undefined) => void
  /**
   * Whether this list IS the routed destination (#2014).
   *
   * `page` renders the shared `PageShell`, which owns the governed `--pad-page`
   * inset and the route's one h1. `embedded` renders the body inside the
   * caller's own frame, and the caller owns both.
   *
   * Required on purpose. Six callers are the route and six are not; a default
   * would give one of those halves two page frames — and Service and
   * Subscriptions, which already render their own h1, a second heading.
   */
  chrome: 'page' | 'embedded'
}

export function ListView({ config, peekId, onPeek, chrome }: ListViewProps) {
  const { entity } = config
  const { density, setDensity } = useTheme()
  const presentation = useGridPresentation(entity)
  const [customizing, setCustomizing] = useState(false)
  const [customizingLayer, setCustomizingLayer] = useState<'tenant' | 'persona'>('tenant')
  const [customizingPersona, setCustomizingPersona] = useState('')
  const [customizingHost, setCustomizingHost] = useState<TablePresentationHost>('console')
  const [customizationFailure, setCustomizationFailure] = useState<unknown>(null)
  /* #1982 — what this entity OFFERS, resolved through the governed declaration
     and the shared resolver. `HOST_LENSES` is this host's renderer inventory,
     not a preference: the console draws all four, so an entity declaring one it
     cannot draw would be a platform gap rather than a console one. */
  const declared = useTablePresentation(entity, HOST_LENSES)
  const selectedPersona = usePersona()
  const views = useViews(entity)
  const viewResolution = useRegistryExperience(entity, 'view')
  const schema = useSchema()
  const identityContracts = useEntityIdentities()
  const navigationEligible = (identityContracts.data ?? [])
    .find((identity) => identity.entity_type === entity)?.navigation_eligible === true
  const sem = useSemantics(entity)
  const risk = useMemo(() => riskMeasures(sem.data?.measures), [sem.data])   // AI Data Highlight (#10)
  const save = useSaveEntity(entity)
  /* The governed acts this entity offers, and where they hang (#1131).
     Behind a seam so #1982's card presentation can place the same acts
     without reaching into this file. */
  const actionSeam = useListActionSeam(config.entity)
  const authoringAuthority = useTablePresentationAuthority()
  const authoringTarget = customizing && (customizingLayer === 'tenant' || customizingPersona)
    ? { entity, persona: customizingLayer === 'persona' ? customizingPersona : null, host: customizingHost }
    : null
  const authoringSupported = customizingHost === 'portal' ? PORTAL_HOST_LENSES : HOST_LENSES

  const authoredPresentation = useTablePresentationSettings(authoringTarget, authoringSupported)
  const authoringCatalogues = useTablePresentationCatalogues(authoringTarget, customizing)
  const targetPersona = customizingPersona
  useEffect(() => {
    if (customizing && customizingLayer === 'persona' && !customizingPersona) {
      const first = authoringCatalogues.personas.data?.[0]?.key
      if (first) setCustomizingPersona(first)
    }
  }, [authoringCatalogues.personas.data, customizing, customizingLayer, customizingPersona])
  const quickCreate = declared.document.quickCreate
  /* The person's own command-button choice, else the governed default (ADR
     F-84 §3). An ABSENT personal value is the "follow the governed default"
     choice — a legacy personal `auto` meant exactly that and now reads absent.
     The table declaration is resolved through system/solution/tenant/persona/
     user-pin, so this one line is the complete cascade rather than a second
     settings hierarchy in the UI. */
  const commandBarPreference = effectiveCommandBar(
    presentation.commandBar, canonicalCommandBar(declared.document.commandBar))
  const quickCreateForm = useResolvedQuickCreateForm(
    entity, selectedPersona || null, quickCreate?.form, Boolean(quickCreate),
  )
  const governedCreateAvailable = Boolean(quickCreate && quickCreateForm.data && schema.data && sem.data)
  const setPresentation = useSetTablePresentation()
  const clearPresentation = useClearTablePresentation()
  /* Inline cell edit is a DIRECT table write (`useSaveEntity` → the SDK's
     EntityHandle), so the entity's registered write door closes it here, in
     the shared list — not only in the generic browser's `config.readOnly`
     (#1131). A bespoke desk that declares an editable column on an `rpc`
     entity would otherwise offer an edit whose commit 403s for everybody.
     `config.readOnly` closes it too: a surface that says it cannot write
     directly must not commit cells either. Deliberately NOT applied to
     `ghost`/`onNew` beyond the existing `config.readOnly` gate — those run
     host-supplied functions, which a desk may legitimately route through a
     governed RPC; `commitCell` alone is always direct DML. */
  const inlineEditClosed = config.readOnly === true || writeDoorOf(schema.data, entity) === 'rpc'
  const pickup = usePickUpWork()
  const [pickupError, setPickupError] = useState<string | null>(null)
  const fork = useForkView(entity)
  const pin = usePinView(entity)

  const entLabel = useMemo(() => ((schema.data?.entities ?? []) as { key: string; label: string }[]).find((e) => e.key === entity)?.label ?? 'Record', [schema.data, entity])
  const sing = config.label ?? singularize(entLabel)

  const [activeId, setActiveId] = useState<string | null>(null)
  const [draft, setDraft] = useState<ViewDoc | null>(null)
  /* The grid reflowed to cards: its header and column ⋮ are inert, so the view
     settings ⋮ carries sort, group and the column chooser instead (#2375). */
  const [headersHidden, setHeadersHidden] = useState(false)
  const [showMcp, setShowMcp] = useState(false)
  const [sel, setSel] = useState<Set<string>>(new Set())
  // The menus this PAGE owns. The column ⋮ and the cell menus belong to the
  // grid and their state went with it (#959); their BODIES still come from
  // here, through <DataGrid>'s render props, so the console's popover and its
  // filter presets are unchanged.
  const [menu, setMenu] = useState<{ kind: 'batch'; anchor: HTMLElement; batchIdx: number } | null>(null)
  // …and whether the GRID has one open. The j/k triage below must stand down
  // for either, and it used to read one piece of state because the grid and the
  // page were one component.
  const [gridMenu, setGridMenu] = useState(false)
  /* The column ⋮ menu's SECOND step (#2375): "Filter…" opens the value editor
     and "Columns…" the chooser, each anchored where the menu was. */
  const [colPanel, setColPanel] = useState<{ kind: 'filter' | 'columns'; column: string; anchor: HTMLElement } | null>(null)
  /* The governed create form is OPEN or it is not. It was a `kick` counter,
     which existed only to re-arm an inline row from elsewhere. */
  const [createOpen, setCreateOpen] = useState(false)
  // The peek aside's DOM id — what a row's aria-controls points at (UX-06).
  const peekDomId = useId()
  /* THE SPLIT (2026-08-07). The detail is a real column beside the grid, not an
     overlay laid over it — Christopher, after using the overlay: "click on
     contact. Screen opens in normal form." The width is the user's and is
     remembered; the divider between them is draggable. */
  const split = useSplitWidth()
  /* A record from a RELATED entity, opened from a band into the same pane. The
     list's own `peek` renders rows of THIS entity; a contact opened from an
     organization's band is a different entity entirely, so the pane renders the
     generic record body for it. One pane, two sources — not two panes. */
  const [related, setRelated] = useState<{ entity: string; id: string } | null>(null)
  /* The related record's NAME used to be kept for the canvas-path crumb, which
     #2375 deletes; the pane names the record itself. */
  const openRelated = (e2: string, id2: string, _name = '') => {
    const canNavigate = (identityContracts.data ?? [])
      .some((contract) => contract.entity_type === e2 && contract.navigation_eligible === true)
    if (!canNavigate) return
    setRelated({ entity: e2, id: id2 })
  }
  // Starts closed and is updated from the governed identity answer later in
  // this render. Event callbacks and effects read the ref at invocation time.
  const navigationEligibleRef = useRef(false)

  /* The assigned persona comes from the server envelope. If an administrator
     changes that assignment, the newly resolved view must not inherit the
     previous persona's unsaved draft. A tab-local persona selection is not
     authority and therefore is deliberately absent from this dependency. */
  useEffect(() => {
    setActiveId(null)
    setDraft(null)
  }, [viewResolution.data?.effectivePersona])

  /* the open peek is the shell drawer's context — the lenses follow the row */
  useEffect(() => {
    if (!peekId || !navigationEligible) return
    setDrawerContext({ entity, id: peekId })
    return () => setDrawerContext(null)
  }, [peekId, entity, navigationEligible])

  const selRef = useRef(sel); selRef.current = sel
  /* ONE definition of this grid's scope, used twice: registered as the AMBIENT
     scope (so a bare ⌘K on the page is already about this list) and handed to
     the grid bar's Marble button, which opens the same thing explicitly. Two
     copies would drift, and the one that drifted would be the button — it is
     the one nobody re-reads. */
  const paletteScopeFor = (): PaletteScope => ({
    entity, label: config.title ?? sing,
      selectionCount: () => selRef.current.size,
      // No create verb behind a shut write door. The ⌘K palette is the OTHER
      // way onto this surface's create — leaving it armed while the toolbar
      // button and the ghost row are both gone would offer a command that
      // silently does nothing, which is the same defect one affordance over
      // (#877).
      /* A surface's OWN create comes first, then a real configured inline create.
         Replacing `＋ New X` with the Marble mark orphaned `config.onNew`
         entirely (2026-08-07): the button that called it was gone and the
         palette only ever armed the ghost row, so CRM's quick-create form and
         CPQ's new-quote dialog became unreachable — a create affordance deleted
         rather than moved, which is the opposite of the trade that justified
         removing the button. Both are offered now; the palette is where a
         surface's whole vocabulary lives. A surface with neither implementation
         exposes no command: incrementing an unrendered ghost row is a silent
         no-op, not a create fallback. */
      ...listCreatePalette({
        readOnly: Boolean(config.readOnly), navigationEligible,
        governedDeclared: Boolean(quickCreate), governedReady: governedCreateAvailable,
        singularLabel: sing, onGovernedCreate: () => setCreateOpen(true),
        onNew: config.onNew,
      }),
    open: (id) => {
      if (!navigationEligibleRef.current) return
      recordRecent(entity, id); onPeek(id)
    },
  })

  useEffect(() => {
    setPaletteScope(paletteScopeFor())
    return () => setPaletteScope(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sing, entity, config.readOnly, config.onNew,
    navigationEligible, quickCreate, governedCreateAvailable,
  ])

  // Entities with no registered core.view still get the advanced list: synthesize
  // a default view from field_meta (is_summary columns, else the first few). This
  // is what makes <ListView> work for ANY entity (the generic browser included).
  const synthColumns = useMemo(() => {
    const fs = (sem.data?.fields ?? []) as { column_name: string; is_summary?: boolean }[]
    const summary = fs.filter((f) => f.is_summary).map((f) => f.column_name)
    return (summary.length ? summary : fs.slice(0, 6).map((f) => f.column_name))
  }, [sem.data])
  const rawViews = ((views.data ?? []) as Row[]).map(asView)
  const effectiveView = viewResolution.data?.state === 'resolved'
    ? asView(viewResolution.data.effective!.document as Row)
    : null
  const persona = viewResolution.data?.effectivePersona ?? selectedPersona
  // …but only once `views` has SETTLED. Synthesizing while the query is still in
  // flight invents a filterless view, which starts a server page for a filter set
  // that is not yet known — a read thrown away the moment the real default view
  // arrives with a chip on it (#385). `views.data != null` is "settled", because
  // an entity with no registered view legitimately resolves to `[]`.
  const synthesizable = views.data != null && viewResolution.data?.state === 'absent' && synthColumns.length > 0
  const inventory = effectiveView && !rawViews.some((view) => view.id === effectiveView.id)
    ? [effectiveView, ...rawViews]
    : rawViews
  const allViews: ViewDoc[] = inventory.length ? inventory
    : (synthesizable ? [{ id: '_default', name: 'All', scope: 'system', persona_key: null, columns: synthColumns, filters: [], sort_spec: [], is_default: true, kind: 'grid' as const, group_by: null, date_col: null }] : [])
  /* Acceptance #2: the switcher offers only lenses the DECLARATION allows —
     RECONCILED, never truncated.

     An entity that declares nothing resolves to `DEFAULT_TABLE_PRESENTATION`,
     which is grid-only. Filtering on that alone deleted every existing board and
     calendar saved view from the switcher the moment this shipped, which is a
     regression dressed as a feature: the customer's saved work would simply
     stop being offered. So when there is NO declaration the offered set is grid
     plus every kind already declared by a saved view — the status quo, stated
     explicitly.

     When a declaration DOES exist it governs, because that is the point of
     declaring one; a saved view outside it is still counted with its reason
     below rather than vanishing. */
  const declaredKinds = declared.undeclared
    ? [...new Set<string>(['grid', ...allViews.map((v) => String(v.kind ?? 'grid'))])]
    : declared.document.availablePresentations.map(String)
  const offeredViews: ViewDoc[] = allViews.filter(
    (v) => declaredKinds.includes(String(v.kind ?? 'grid')))
  /* Activation reads the OFFERED set. Reading `allViews` left an unavailable
     view selected while the switcher no longer offered it — the surface then
     drew a lens the declaration had withdrawn, and nothing said why. */
  const resolvedIdRaw = effectiveView?.id ?? allViews[0]?.id
  const resolvedId = offeredViews.some((v) => v.id === resolvedIdRaw)
    ? resolvedIdRaw
    : offeredViews[0]?.id ?? resolvedIdRaw
  const currentId = activeId ?? resolvedId
  const base = allViews.find((v) => v.id === currentId)
    ?? (currentId === effectiveView?.id ? effectiveView : undefined)
  const view = draft ?? base
  /* `_default` is host synthesis, not a registered view choice. Asking for its
     convenient grid shape would override the table declaration's governed
     default; null means "resolve the declaration default". */
  const requestedKind = view?.id === '_default' ? null : (view?.kind ?? null)
  const requestedPresentation = useRequestedTablePresentation(
    entity, persona || null,
    requestedKind as import('@solum/experience').TablePresentation | null, HOST_LENSES,
  )
  /* A non-null saved-view request has no optimistic answer. The query joins the
     list boundary below, so this neutral value is never rendered while pending
     and the declaration default cannot flash before the governed refusal. */
  const requestedLensDegraded = Boolean(requestedKind != null && requestedPresentation.isError)
  /* Classify the pending state through the same boundary primitive as the page.
     A terminal declaration failure intentionally degrades to the grid and is
     named below, so this small query shape contributes loading only. */
  const declarationState = queryState([{ isLoading: declared.isLoading, isError: false, error: null, data: true }])
  const declarationPending = requestedKind == null && declarationState.kind === 'loading'
  /* A failed optional presentation lookup cannot make already-governed rows
     unreadable.  Pending still joins the page boundary (so the wrong default
     lens never flashes); a terminal transport/refusal answer degrades to the
     only universally safe renderer and is named beside the rows below. */
  const activePresentation = requestedKind == null
    ? declared
    : !requestedLensDegraded ? requestedPresentation.data : undefined
  const activeLens = activePresentation?.active ?? 'grid'
  const cardActionIds = activeLens === 'cards' ? (activePresentation?.document.card?.actions ?? []) : undefined
  const pinnedId = viewResolution.data?.effective?.logicalKey.startsWith('view-pin:')
    ? viewResolution.data.effective.id ?? undefined
    : undefined

  const fields = useMemo(() => new Map((sem.data?.fields ?? []).map((f) => [f.column_name, f])), [sem.data])

  /* Reference identity for exactly the ids on THIS page (#1993).
     WHAT each column points at is DERIVED from the registered relationship
     metadata; the label comes from core.titles_of under this session's own RLS.
     ListConfig.refs stays supported and WINS, as a presentation override — it is
     no longer what makes an ordinary reference cell correct. */
  const refTargets = useReferenceTargets(entity)
  /* The MONOMORPHIC targets, as the flat map the column filter's ref picker
     needs. A polymorphic column is deliberately absent: its target is a per-row
     fact, and one picker cannot stand for two entities. */
  const refEntities = useMemo(() => {
    const out: Record<string, string> = {}
    for (const [column, t] of refTargets.byColumn) if (t.target_entity_type) out[column] = t.target_entity_type
    return { ...out, ...(config.refs ?? {}) }
  }, [refTargets.byColumn, config.refs])
  /* Whether a column holds a REFERENCE at all — polymorphic ones included, which
     is why this is not `refEntities[column] != null`. A cell that holds a
     reference must never print its own value, because that value is a UUID. */
  const isReference = useCallback(
    (column: string) => refTargets.byColumn.has(column) || (config.refs ?? {})[column] != null,
    [refTargets.byColumn, config.refs])

  /* ── the column ⋮ filter's TYPE information (#959) ───────────────────────
     One resolution, used for two things that must never disagree: which
     operators the menu OFFERS, and which SQL operator family the translation
     RUNS. Two answers to "what kind of column is this" is how `ilike` reaches a
     numeric column and the list 400s where it used to show rows. */
  const statusMachine = useEntityStatusMachine(entity)
  /* A SUBJECT entity (#2444 slice 4) is read through a governed door with a
     CLOSED vocabulary — exact values, one sort column, 200-row pages, no count
     — and every row it returns is a `record.viewed`. So its column menu offers
     only what that door answers faithfully (and says why), and its views are
     translated for that door or REFUSED on screen; they never fall back to
     filtering a fetched page in the browser, which would record every row of
     that page to show a few of them. */
  const subject = useIsSubjectEntity(entity)
  const filterFieldFor = useMemo(() => {
    const machine = statusMachine.data ?? null
    return (column: string) => {
      const field = resolveColumnFilterField({ column, fields, machine, refs: refEntities })
      return subject ? subjectFilterField(field) : field
    }
  }, [fields, statusMachine.data, refEntities, subject])
  const kindOf = useMemo(() => kindResolver(fields), [fields])
  /* The subject translation reads the MENU's own kind for a column (a status
     machine makes a text column an option column), so what the menu offers
     and what the list applies are one answer. */
  const subjectPlan = useMemo(() => {
    if (!view || !subject) return null
    const kindForMenu = (column: string) => {
      const k = filterFieldFor(column).kind
      return k === 'unknown' ? undefined : k
    }
    return {
      translation: leavesToSubjectFilters((view.filters ?? []) as never[], { kindOf: kindForMenu, binds: { $me: me() } }),
      sort: sortToSubject(view.sort_spec),
    }
  }, [view, subject, filterFieldFor])
  const subjectRefusal = subjectPlan && !subjectPlan.translation.ok ? subjectPlan.translation : null

  // ── #113: filter and sort on the SERVER when the view translates faithfully ──
  // Doing this in the browser over a fetched page does not merely truncate — it
  // answers the wrong question. Measured on 204,560 invoices, a `status = paid`
  // chip reported 125 matches against a true 50,022, and a sort-by-value view
  // never showed the largest record.
  //
  // `filtersToPostgrest` returns null when ANY leaf lacks a faithful equivalent
  // ($me binds, `empty`, boolean composites), and then the view is evaluated
  // client-side exactly as before. Correct-and-partial beats wrong-and-complete.
  //
  // Since #959 the translation is also told each field's KIND, from the same
  // `field_meta` read that decides which operators the column ⋮ offers. `ilike`
  // is a text operator; `numeric ~~* unknown` does not exist, so a column menu
  // that can author "Amount = 100" without this answers 400 where the list used
  // to show rows.
  const pushedBase = useMemo(() => {
    if (!view) return null
    if (subject) {
      if (!subjectPlan?.translation.ok) return null
      return { filters: subjectPlan.translation.filters, order: subjectPlan.sort.order, limit: SUBJECT_PAGE_SIZE }
    }
    const filters = filtersToPostgrest((view.filters ?? []) as never[], { kindOf })
    if (!filters) return null
    return { filters, order: sortToPostgrest(view.sort_spec) ?? undefined, limit: PAGE_SIZE }
  }, [view, kindOf, subject, subjectPlan])

  /* ── SEARCH (#2375) — on every interactive grid, scoped to THIS grid's rows ──
     A case-insensitive substring of any visible TEXT column, asked of the SERVER
     wherever the view is (so it composes with the view's filters and the page's
     total, and a group's count includes it), and of the evaluated pool where the
     view is client-side. Reference columns are not searched: the row holds a
     uuid, and a search that matched a label the row does not carry would need a
     second read path. A subject entity's governed door has a closed vocabulary
     with no free text, so its search is present and says it cannot search — it
     never filters a fetched page in the browser (#2444). */
  const [search, setSearch] = useState('')
  const [searched, setSearched] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setSearched(search.trim()), 250)
    return () => clearTimeout(timer)
  }, [search])

  /* ── THE ADDRESS (#2375 §11) ───────────────────────────────────────────────
     A root list is `?view=&q=&sort=name,-kind&f=<encoded>`. The URL is the
     state: it is ADOPTED (opening a link, Back, Forward) and it is WRITTEN
     canonically (a push for view/sort/filter so Back restores them, a replace
     while typing a search), keeping the route's own keys. Embedded lists never
     touch the address. */
  const gridUrl = useGridUrl(chrome === 'page')
  const urlKey = JSON.stringify(gridUrl.url)
  const adoptedUrl = useRef<string | null>(null)
  const adoptingNow = useRef(false)
  useEffect(() => {
    if (chrome !== 'page' || !base) return
    if (adoptedUrl.current === urlKey) return
    const url = gridUrl.url
    let changed = false
    const wantView = url.view && allViews.some((v) => v.id === url.view) ? url.view : null
    if ((wantView ?? resolvedId ?? null) !== (currentId ?? null)) {
      setActiveId(wantView); setDraft(null); setSel(new Set())
      adoptingNow.current = true
      return // the next render adopts sort/filter against the view it switched to
    }
    const savedTerms = parseSortSpec(base.sort_spec)
    const wantSort = url.sort ? decodeSortParam(url.sort) : savedTerms
    const wantFilters = (url.f ? decodeFilterParam(url.f) : null) ?? base.filters
    const current = draft ?? base
    const sortDiffers = encodeSortParam(parseSortSpec(current.sort_spec)) !== encodeSortParam(wantSort)
    const filtersDiffer = JSON.stringify(current.filters) !== JSON.stringify(wantFilters)
    if (sortDiffers || filtersDiffer) {
      setDraft({
        ...current, columns: [...current.columns],
        sort_spec: sortDiffers ? encodeSortSpec(wantSort) : [...current.sort_spec],
        filters: filtersDiffer ? wantFilters.map((f) => ({ ...f })) : current.filters.map((f) => ({ ...f })),
      })
      changed = true
    }
    if ((url.q ?? '') !== search.trim()) { setSearch(url.q ?? ''); changed = true }
    adoptedUrl.current = urlKey
    adoptingNow.current = changed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chrome, urlKey, base?.id])
  useEffect(() => {
    if (chrome !== 'page' || !base || !view) return
    if (adoptedUrl.current !== urlKey) return
    if (adoptingNow.current) { adoptingNow.current = false; return }
    const next = canonicalGridUrl({
      viewId: currentId ?? null, defaultViewId: resolvedId ?? null,
      q: search,
      sort: parseSortSpec(view.sort_spec), savedSort: parseSortSpec(base.sort_spec),
      filters: view.filters, savedFilters: base.filters,
    })
    if (sameGridUrl(next, gridUrl.url)) return
    const onlySearch = (next.view ?? '') === (gridUrl.url.view ?? '') && (next.sort ?? '') === (gridUrl.url.sort ?? '')
      && (next.f ?? '') === (gridUrl.url.f ?? '')
    adoptedUrl.current = JSON.stringify(next)
    gridUrl.write(next, onlySearch ? 'replace' : 'push')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chrome, currentId, resolvedId, search, view, base, urlKey])
  const searchColumns = useMemo(() => (view?.columns ?? []).filter((column) =>
    SEARCHABLE_KINDS.has(String((fields.get(column) as { kind?: string } | undefined)?.kind ?? ''))
      && !isReference(column)), [view, fields, isReference])
  const searchUnavailable = subject
    ? `${pluralize(entLabel)} are read through a governed door that cannot search.`
    : searchColumns.length === 0 ? 'This view shows no text column to search.' : undefined
  const searchGroup = searched && !searchUnavailable ? searchCondition(searched, searchColumns) : null
  const pushed = useMemo(() => {
    if (!pushedBase || subject || !searchGroup) return pushedBase
    return { ...pushedBase, filters: [...pushedBase.filters, ...combineOrGroups([searchGroup])] }
  }, [pushedBase, subject, searchGroup])

  /* ── GROUPED ROWS (#2375, ADR F-84 §2) ─────────────────────────────────────
     One field, from the governed `core.view.group_by` — a governed default is
     simply a grid view that names one, and a person's saved view carries it
     through the fork. Only an ENUMERABLE field groups (the platform's own
     rule, `core.assert_view_kind_shape`), and only where this list can state
     EXACT whole-result totals:
       · a server-evaluated view  → one bounded count per value + per-group
                                    slices (`useGroupedEntityPage`);
       · a client-evaluated view  → in memory, ONLY when the pool is complete;
       · a subject door, a capped pool, a non-enumerable field → not offered,
         and a stored grouping says so on screen instead of grouping a page. */
  const groupDomainFor = useCallback((column: string): GroupDomain | null => {
    const field = filterFieldFor(column)
    if (field.kind === 'option' && field.options && field.options.length > 0) {
      return optionGroupDomain(column, field.options)
    }
    if (String((fields.get(column) as { kind?: string } | undefined)?.kind ?? '') === 'boolean') {
      return booleanGroupDomain(column)
    }
    return null
  }, [filterFieldFor, fields])
  const groupBy = activeLens === 'grid' ? (view?.group_by ?? null) : null
  const groupDomain = groupBy ? groupDomainFor(groupBy) : null
  const groupDirection: 'asc' | 'desc' = groupBy
    && parseSortSpec(view?.sort_spec).find((term) => term.key === groupBy)?.direction === 'desc' ? 'desc' : 'asc'
  const serverCollection = serverPagedCollection({
    exactTotal: !subject && pushedBase != null,
    serverGrouping: !subject && pushedBase != null,
    savedViewWrites: true,
    commands: true,
  })
  const serverGrouping = resolveCollectionGrouping({
    adapter: serverCollection,
    requestedColumn: groupBy,
    enumerable: groupDomain != null,
  })
  const groupMode: 'none' | 'server' | 'client' | 'unsupported' = !groupBy ? 'none'
    : pushedBase != null && serverGrouping.kind === 'supported' ? 'server'
      : subject || !groupDomain ? 'unsupported'
        : 'client'
  const groupOrder = groupBy
    ? sortToPostgrest(encodeSortSpec(parseSortSpec(view?.sort_spec).filter((term) => term.key !== groupBy))) ?? undefined
    : undefined

  // A cursor is meaningful only for the exact governed query that produced it.
  // Include view/persona as well as the translated transport shape: switching
  // to a different workflow view must begin at its first state even when its
  // current filters happen to be identical (#882). The grouping is part of the
  // query's shape too: a grouped page 3 is not an ungrouped page 3.
  const pagingKey = JSON.stringify([entity, persona, currentId, pushed, groupMode === 'server' ? groupBy : null])
  const [cursor, setCursor] = useState<PageCursor>({ queryKey: pagingKey, offset: 0 })
  const offset = pageOffset(cursor, pagingKey)

  // Nothing is fetched until a view is RESOLVED. `pushed` is null both when the
  // view cannot be pushed down AND while the view is still unknown, so gating on
  // `!serverSide` alone enabled the client page on the very first paint — before
  // anything could know it was not needed — and the 500 rows the comment below
  // says are not pulled were pulled on every cold load (#385). Measured in the
  // browser on a cold /billing, /contracts and /commerce: TWO reads per list
  // before (a 500-row client page, then the server page), ONE after.
  const page = useEntityPage(entity, {
    ...(pushed ?? { limit: PAGE_SIZE }),
    offset,
    // A refused subject view reads NOTHING: there is no honest page to fetch.
    // A server-GROUPED view reads its groups instead (below).
    enabled: view != null && subjectRefusal == null && groupMode !== 'server',
  })
  const grouped = useGroupedEntityPage(entity, {
    filters: pushedBase?.filters ?? [],
    searchGroup,
    domain: groupDomain ?? EMPTY_GROUP_DOMAIN,
    direction: groupDirection,
    order: groupOrder,
    offset,
    limit: PAGE_SIZE,
    enabled: groupMode === 'server',
  })
  const serverSide = pushed != null
  /** The view is known AND it does not translate — the browser really is the filter.
   *  Never for a subject entity: its refusal is said on screen instead. */
  const clientSide = view != null && !serverSide && !subject

  // The client-side path only fetches when it is actually going to be used. This
  // used to run UNCONDITIONALLY alongside the server-side page — every list load
  // pulled 500 rows it then threw away.
  const rows = useEntityRows(entity, PAGE_SIZE, clientSide)
  /* A client-evaluated pool groups only when it is COMPLETE: a full pool is the
     first 500 of an unknown number, and grouping it would imply totals nobody
     measured. */
  const clientCollection = cappedClientCollection({
    rowCount: rows.data?.length ?? 0,
    limit: PAGE_SIZE,
    reason: `this view is evaluated over the first ${PAGE_SIZE} rows`,
  })
  const clientGrouping = resolveCollectionGrouping({
    adapter: clientCollection,
    requestedColumn: groupBy,
    enumerable: groupDomain != null,
  })
  const effectiveGroupMode = groupMode === 'client' && rows.data != null && clientGrouping.kind === 'unsupported'
    ? 'unsupported' : groupMode
  const groupUnsupportedReason = effectiveGroupMode !== 'unsupported' ? null
    : subject ? `${pluralize(entLabel)} are read through a governed door that does not count groups`
      : !groupDomain ? 'it is not a field with a fixed set of values'
        : clientGrouping.kind === 'unsupported' ? clientGrouping.reason : serverGrouping.kind === 'unsupported'
          ? serverGrouping.reason : 'this collection cannot group exactly'

  const clientData = useMemo(() => {
    if (!view || serverSide) return []
    const evaluated = applyView((rows.data ?? []) as Row[], view)
    if (!searched || searchUnavailable) return evaluated
    return evaluated.filter((row) => matchesCollectionText(
      searched, ...searchColumns.map((column) => row[column]),
    ))
  }, [view, serverSide, rows.data, searched, searchUnavailable, searchColumns])
  const clientGrouped = useMemo(() => effectiveGroupMode === 'client' && groupDomain
    ? groupRowsInMemory(clientData, groupDomain, (row) => row[groupDomain.column], groupDirection)
    : null, [effectiveGroupMode, groupDomain, clientData, groupDirection])

  const data = useMemo(() => {
    if (!view) return []
    // Server-side: the rows already ARE the filtered, sorted page. Re-running
    // applyView over them would be a no-op at best and, for the operators whose
    // JS and SQL semantics differ, a second contradictory filter.
    if (effectiveGroupMode === 'server') return grouped.data.rows as Row[]
    if (serverSide) return ((page.data?.rows ?? []) as Row[])
    return clientGrouped ? clientGrouped.rows : clientData
  }, [view, effectiveGroupMode, grouped.data.rows, serverSide, page.data, clientGrouped, clientData])

  /* …and the LABEL for each reference on this page, batched through
     core.titles_of under this session's own RLS (#1993). Kept here rather than
     beside refTargets above because it reads the page that is on screen. */
  const refLabel = useRefLabels(entity, config.refs, data)
  const orderedIds = data.map((d) => String(d.id))
  const identityRefs = useMemo(
    () => data.map((row) => ({ type: entity, id: String(row.id) })),
    [data, entity],
  )
  const recordIdentities = useRecordIdentities(identityRefs)

  // ── the peeked row must survive LEAVING the page it was opened from ────────
  // Resolving a case drops it out of the "Open cases" view, and refunding a
  // return drops it out of "Open returns" — while the user is still looking at
  // the panel that did it. This was covered by the 500-row client page, which
  // was fetched on every load and searched here as a fallback; #385 stopped
  // fetching it and two browser specs went red, which is the honest way to find
  // out that a discarded read was load-bearing.
  //
  // The replacement is KEYED, not capped — the same doctrine as `useLookupLabels`:
  // one row, by id, enabled only when the row really is off the page. It is also
  // strictly more correct than the pool it replaces, which found the row only if
  // it happened to sort inside the first 500. `useSaveEntity` and the desks'
  // governed RPCs both invalidate `['row', entity, …]`, so the panel shows the
  // row AFTER the transition rather than the copy it was opened with.
  // MUST be a hook up here, above the early `if (listState…) return` — see the
  // `activeRow` scar for what adding one below it costs.
  const onPage = peekId ? data.find((d) => String(d.id) === peekId) ?? null : null
  const offPage = useEntityRow(entity, peekId ?? 'new', peekId != null && onPage == null)
  const widths = config.widths ?? {}
  const colWidths = useColumnWidths(entity)
  /* A surface's own wording wins; otherwise the chip says what the column ⋮
     menu says, in the same words — `Status is any of Open, Pending` rather than
     `status in open,pending`. The raw shape is the last resort, for a leaf the
     resolver cannot describe. */
  const chipLabel = config.chipLabel
    ?? ((f: Leaf) => (f.field ? summarizeColumnFilter(filterFieldFor(f.field), [f as ColumnFilterLeaf]) : null) ?? defaultChipLabel(f))

  /* j/k + Esc triage (input-guarded) */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^(INPUT|TEXTAREA|SELECT)$/.test((e.target as HTMLElement).tagName)) return
      if (menu || gridMenu || colPanel) return
      if (!peekId) { if (e.key === 'Escape' && sel.size) setSel(new Set()); return }
      const i = orderedIds.indexOf(peekId)
      if (e.key === 'j' || e.key === 'ArrowDown') { e.preventDefault(); const n = orderedIds[Math.min(orderedIds.length - 1, i + 1)]; if (n) onPeek(n) }
      else if (e.key === 'k' || e.key === 'ArrowUp') { e.preventDefault(); const n = orderedIds[Math.max(0, i - 1)]; if (n) onPeek(n) }
      else if (e.key === 'Escape') onPeek(undefined)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  // Spinner-or-nothing: a failed fetch looked exactly like an empty list, so a
  // user could not tell "no deals" from "not authorised" or "module missing"
  // (UX-03). The boundary names the difference and offers a retry only where
  // retrying can actually help.
  //
  // …and it has to watch the query the grid is actually RENDERING FROM (#385).
  // It watched `rows` unconditionally while `data` came from `page`, so a server
  // page that was still in flight — or had failed outright — was reported as
  // `ready` and drew an EMPTY LIST: "there are no invoices", asserted while the
  // request was open or broken. `page.data` is rebuilt by `pageResult` and is
  // never null, so the boundary's own settled-with-no-data check could not catch
  // it either.
  //
  // The list only survived at all because the disabled `rows` query was
  // load-bearing by accident: TanStack v5 leaves a disabled query at
  // `isLoading:false, data:undefined`, which `queryState` classifies as an ERROR
  // — and the only reason that error never showed is that `views` is still
  // loading on first paint, so `rows` was briefly enabled and fetched after all.
  //
  // ONE binding, used for both the classification and the boundary: the two used
  // to be written out separately, and a pair of hand-copied triples that must
  // agree is the shape the defect had.
  // A twist is offered only where the metamodel says there is something behind
  // it. An entity with no registered list slots keeps the list it always had —
  // which is what makes bands safe to have on by default.
  //
  // Memoized: `deriveRelationSlots` walks every registered relationship and
  // builds two Maps, and this runs on EVERY ListView render — cell edits, sort
  // clicks, selection changes. The answer only moves when the schema does.
  //
  // ABOVE THE EARLY RETURN, and that placement is the point. It used to sit
  // ~100 lines further down, after the `listState.kind !== 'ready'` bail-out —
  // so the loading render called one fewer hook than the ready render, and React
  // counts hooks by position. That is "Rendered more hooks than during the
  // previous render" waiting for a list whose first paint is a spinner, which is
  // every list. eslint's rules-of-hooks caught it; nothing else would have until
  // it crashed in front of somebody.
  const bandSlots = useMemo(() => listSlots(schema.data, entity), [schema.data, entity])

  /* Identity is an enrichment, not permission to see the list. The governed
     door deliberately answers nothing for a hidden/undeclared title, and a
     page change must not unmount the grid (which would strand keyboard focus)
     while the next title batch is in flight. `useRecordIdentities` supplies
     neutral copy until it settles. */
  const boundaryQueries: QueryLike[] = [
    ...(clientSide
      ? [views, viewResolution, rows, sem, refTargets.query, identityContracts]
      : [views, viewResolution, effectiveGroupMode === 'server' ? grouped : page, sem, refTargets.query, identityContracts]),
    ...(requestedKind != null && !requestedLensDegraded ? [requestedPresentation] : []),
    ...(declarationPending ? [{ isLoading: true, isError: false, error: null }] : []),
  ]
  const listState = queryState(boundaryQueries)
  /* Hooks below: ABOVE the waiting arm's early return — a hook after it is
     "rendered more hooks than the previous render" and a blank page. */
  const governedCreateReady = Boolean(quickCreate && quickCreateForm.data && schema.data && sem.data)
  /* THE COMMAND BUTTONS (#2375 §3.3, commands v5). The presentation may select,
     order and label commands; it never grants one. Each resolves against what
     THIS caller's governed registry offers — the governed quick create, and the
     entity's `cardinality = 'none'` acts — and an unresolvable one is dropped.
     With no `commands` declared, the derived list is "New" when quick create
     is ready. A derived New keeps the list's own words ("New invoice"). */
  const resolvedCommands = useMemo(() => {
    const resolved = resolveTableCommands({
      document: activePresentation?.document,
      governed: {
        quickCreate: governedCreateReady,
        linkExisting: false,
        actions: new Set(actionSeam.commandChoices.map((choice) => choice.key)),
      },
    })
    return resolved.source === 'derived'
      ? resolved.commands.map((command) => command.action === 'quickCreate'
        ? { ...command, label: `New ${sing.toLowerCase()}` } : command)
      : resolved.commands
  }, [activePresentation?.document, governedCreateReady, actionSeam.commandChoices, sing])
  const collectionCommands = useCollectionCommands(config.entity, resolvedCommands, {
    quickCreate: governedCreateReady ? () => setCreateOpen(true) : undefined,
    testIdOf: (command) => command.action === 'quickCreate' ? 'list-new' : `list-command-${command.id}`,
  })
  const showCommands = commandBarPreference === 'shown' && collectionCommands.count > 0
  if (listState.kind !== 'ready' || !view) {
    /* #2014 — the WAITING page is a page too. This branch used to return a bare
       `.page` div: no shared inset, and no h1 at all, so for as long as the
       governed doors took to answer, the route had no heading. The success
       branch below was the only one anybody had looked at. */
    const waiting = (
      <QueryBoundary queries={boundaryQueries} label={pluralize(sing).toLowerCase()}>{() => <Spinner />}</QueryBoundary>
    )
    if (chrome === 'embedded') return waiting
    return (
      <PageShell width="full" testId="list-page" title={config.title ?? pluralize(entLabel)}>
        {waiting}
      </PageShell>
    )
  }

  const mutate = (fn: (v: ViewDoc) => void) => {
    const nextBase = draft ?? base!
    const next: ViewDoc = { ...nextBase, columns: [...nextBase.columns], filters: nextBase.filters.map((f) => ({ ...f })), sort_spec: [...nextBase.sort_spec] }
    fn(next)
    if (base!.scope === 'user') { setDraft(null); setActiveId(base!.id); void fork }
    setDraft(next)
  }
  const saveFork = async () => {
    if (!draft) return
    const created = await fork.mutateAsync({ ...draft, name: draft.scope === 'user' ? draft.name : 'My ' + draft.name.toLowerCase().replace(/^my /, '') } as unknown as Row)
    setDraft(null); setActiveId(String((created as Row).id))
  }
  // The green flash is the list SAYING the write landed. It used to be three
  // unsequenced statements — `void save.mutateAsync(...)` discarded the promise
  // and the flash fired regardless — so a 403 on a revoked table was confirmed
  // as a success and reverted silently on the next refetch (#877).
  //
  // The flash itself is the GRID's now (#959), and the contract that keeps the
  // fix is the return value: <DataGrid> flashes iff this resolves `true`, which
  // is exactly what `confirmedWrite` means. A grid that flashed on being CALLED
  // would have re-opened the defect one component over.
  const commitCell = (row: Row, field: string, value: unknown): Promise<boolean> =>
    confirmedWrite(
      () => save.mutateAsync({ id: String(row.id), patch: { [field]: value } }),
      () => {},
    )

  navigationEligibleRef.current = navigationEligible
  const openPeek = (id: string) => {
    if (!navigationEligible) return
    recordRecent(entity, id); setRelated(null); onPeek(id)
  }

  /** What a row is CALLED — the first summary-ish text on it, so a screen reader
   *  announces "Q4 rollout, row 3" rather than "row 3". */
  const entityIdentity = recordIdentities.entityIdentity.get(entity)

  const rowName = (d: Row): string => {
    const identity = recordIdentities.identity(entity, d.id)
    return identity.title
  }

  /* Row-body activation opens the split preview. Only the displayed identity
     opens the canonical full record. Resolve which view column already carries
     that identity instead of assuming the first column is always a name. */
  const identityColumn = view.columns.find((column) =>
    data.some((row) => String(row[column] ?? '').trim() === rowName(row).trim()))

  const pickUp = async (row: Row) => {
    setPickupError(null)
    try {
      await pickup.mutateAsync({ entity, id: String(row.id) })
    } catch (error) {
      setPickupError(pickupRefusal(error))
    }
  }

  const queueRows = data.filter(isQueuePickupRow)

  const canBand = config.relatedBands !== false && bandSlots.length > 0
  /* A user's dragged width wins over the surface's declared one, which wins over
     the 120px floor. `colWidths` is per USER and per ENTITY (core.user_setting) —
     not part of the view, because two people sharing a view should not have to
     agree on how wide their monitors are. The grid resolves the override; the
     surface's DECLARED width is the page's answer and is handed over per column. */
  const declaredWidth = (c: string) => widths[c] ?? config.columns[c]?.width ?? '120px'
  const peekRow = onPage ?? (peekId ? ((offPage.data as Row | null) ?? null) : null)
  const sortTerms = parseSortSpec(view.sort_spec)

  /* A surface the module has declared is NOT a record has nothing to open
     (#1993). `core.entity_type.non_record_surface` carries the REASON, so this
     is a decision somebody made and wrote down — not an inference from a
     missing title, which is a different fact and looked identical until now.
     The row still shows its name; it just is not a link, and it does not enter
     the recents. */
  const nonRecordSurface = entityIdentity?.non_record_surface ?? null
  const recordIdentityCell = (row: Row) => (!navigationEligible
    ? (
      <span className={lv.recordIdentityLink} data-testid={`record-identity-${String(row.id)}`}
        data-non-record-surface={nonRecordSurface ?? 'record-navigation-unavailable'}>
        <EntityIcon iconKey={entityIdentity?.icon_key} size={16} />
        <span>{rowName(row)}</span>
      </span>
    )
    : (
    <Link className={lv.recordIdentityLink} data-testid={`record-identity-${String(row.id)}`}
      data-record-link="" tabIndex={-1}
      to="/e/$entity/$id" params={{ entity, id: String(row.id) }}
      aria-label={`Open the full record: ${rowName(row)}`}
      onClick={(event: MouseEvent<HTMLAnchorElement>) => { event.stopPropagation(); recordRecent(entity, String(row.id)) }}>
      <EntityIcon iconKey={entityIdentity?.icon_key} size={16} />
      <span>{rowName(row)}</span>
    </Link>
  ))

  /* The view, resolved into what a grid needs: a label, a width, a sort state and
     a filtered flag per column. Everything the CONSOLE knows and a grid must not
     — field_meta labels, the saved view's sort_spec, its filter leaves — is
     answered here and handed over as data. */
  /* Column roles for the cards lens, DERIVED from the governed recipe — never
     typed into this page (rule 42). `undefined` for every other lens, so card
     roles cannot leak onto a grid.

     Derived HERE rather than returned by `useTablePresentation`, deliberately:
     a function on a hook's return value is a shape every test that stubs the
     hook has to remember, and the first version of this broke fifteen ListView
     tests with `declared.cardRoles is not a function`. The hook answers with
     data; the host does the derivation. */
  const cardRoles = activeLens === 'cards'
    ? cardColumnRoles(activePresentation?.document?.card, view.columns)
    : undefined
  /* …and an OPTION column prints its registered LABEL, never its stored key
     (#2406 rogue census, machine-text: "on_hold", "in_review" in the generic
     /e/afd_project and /e/kyc_case lists). The labels come from the one place
     the column filter already reads them — the entity's status machine for its
     status column, else the field's registered options — so the cell and the
     filter chip cannot name one state two ways. A value outside the registered
     set is said to be so (`optionLabel`), not printed bare. */
  const optionCell = (column: string) => {
    const field = filterFieldFor(column)
    const options = field.kind === 'option' ? field.options : undefined
    if (!options?.length) return undefined
    return (row: Row) => <span>{optionLabel(row[column], options)}</span>
  }
  /* ONE name per column (#2375): the header, its ⋮ menu, the filter editor and
     the applied-filter pill all say the same words. A column the registry never
     labelled reads as its humanised key — never the raw snake_case key in a pill
     beside an upper-cased header that looked different. */
  const columnLabel = (column: string): string => {
    const label = fields.get(column)?.label
    return label ? String(label) : humanizeKey(column)
  }
  const gridColumns: GridColumn[] = view.columns.map((c) => {
    const colCfg = config.columns[c] ?? {}
    const ordinaryCell = colCfg.cell
      ? (row: Row, ctx: GridCellCtx) => colCfg.cell!(row, {
          ...ctx,
          refLabel: (id, fallback) => refLabel(c, id, fallback, row),
          refLabelOf: (column, id, fallback) => refLabel(column, id, fallback, row),
        })
      : undefined
    return {
      key: c,
      label: columnLabel(c),
      width: declaredWidth(c),
      align: colCfg.align,
      editable: colCfg.editable && !inlineEditClosed,
      editKind: colCfg.editKind,
      hasMenu: colCfg.menu != null,
      sort: sortTerms.find((term) => term.key === c)?.direction === 'asc' ? 'ascending'
        : sortTerms.some((term) => term.key === c) ? 'descending' : 'none',
      sortOrder: sortTerms.findIndex((term) => term.key === c) + 1 || undefined,
      filtered: view.filters.some((f) => f.field === c),
      /* …and WHAT it is filtered by, in words. The flag alone tinted the ⋮ and
         nothing else: a colour-only signal that told a screen-reader user
         nothing at all about why the count disagreed with the total. */
      filterSummary: summarizeColumnFilter(filterFieldFor(c), view.filters as ColumnFilterLeaf[],
        (v) => refLabel(c, v, '') || undefined) ?? undefined,
      /* THE CARD RECIPE WINS WHEN THE CARD LENS IS ACTIVE (#1982). Otherwise
         the host's own compact roles stand, which is what the responsive
         compact grid has always used. The identity column stays primary in
         both — a card with no title is not a card. */
      compact: cardRoles?.[c] ?? (c === identityColumn ? 'primary' : colCfg.compact),
      /* The identity column is the row's NAME by definition, whatever the
         host declared — `identityColumn` is resolved by matching a column's
         value against the governed `rowName`. Saying so here means the
         weight is the same whether or not `recordIdentityCell` replaces the
         host's body below, which it does for exactly this column. */
      emphasis: c === identityColumn ? 'name' : colCfg.emphasis,
      // The surface's own renderer, with the REFERENCE resolvers the grid has no
      // business holding: `refLabel` is keyed on the column being rendered and
      // `refLabelOf` on any other, because a column does not have to display its
      // own value (Subscriptions renders `plan_id`'s name inside `quantity`).
      /* …and where the surface has NO renderer of its own, a reference column
         still must not print its value. The grid's generic body is
         `String(row[key])` by design — DataGrid has no schema or data
         dependency and #1993 says to keep it that way — so the governed text is
         supplied here, from the metamodel the console already holds. An
         unresolved target (missing, deleted, or not visible to this session)
         renders an em dash, never the id. */
      cell: c === identityColumn
        ? recordIdentityCell
        : ordinaryCell ?? (isReference(c)
          ? (row: Row) => <span>{refLabel(c, row[c], '—', row)}</span>
          : optionCell(c)),
    }
  })

  const actionColumn = actionSeam.columnFor(cardActionIds)
  if (actionColumn) gridColumns.push(actionColumn)

  if (!identityColumn) {
    /* A composed title may not equal any single returned column. It still needs
       one honest, visible home; never relabel a status/date column as the name
       and never fall back to the UUID. This structural column is not persisted
       into the saved view, just like selection/expansion controls are not. */
    gridColumns.unshift({
      key: '__record_identity', label: sing, width: '220px', sortable: false, menuable: false,
      compact: 'primary',
      cell: recordIdentityCell,
    })
  }
  if (queueRows.length > 0) {
    gridColumns.push({
      key: '__queue_pickup', label: 'Queue', width: '108px', sortable: false, menuable: false,
      compact: 'actions',
      cell: (row) => isQueuePickupRow(row) ? (
        <Button size="sm" variant="ghost" data-testid={`pick-up-${String(row.id)}`}
          disabled={pickup.isPending}
          onClick={(event) => { event.stopPropagation(); void pickUp(row) }}>
          {pickup.isPending && pickup.variables?.id === String(row.id) ? 'Picking up…' : 'Pick up'}
        </Button>
      ) : null,
    })
  }

  const closeMenu = () => setMenu(null)

  /* A calendar is a LENS over the governed query, never a second data source.
     `date_col` is registered in core.view and validated against the entity's
     date/timestamp columns by core.assert_view_kind_shape. A nullable date has
     nowhere honest to be placed, and a malformed transport value must not be
     silently invented onto "today", so both are omitted from the lens while the
     row remains available in the grid view.

     Deliberately absent: onSelectDate/onMoveEvent. The generic list knows that a
     row is visible; it does not thereby know a governed create or reschedule
     verb. Event activation is the same record-open callback the grid uses. */
  const calendarEvents: CalendarEvent[] = activeLens === 'calendar' && view.date_col
    ? data.flatMap((row) => {
        const raw = row[view.date_col!]
        if (raw == null || raw === '') return []
        const start = new Date(String(raw))
        if (Number.isNaN(start.getTime())) return []
        return [{ id: String(row.id), title: rowName(row), start }]
      })
    : []

  /* A board is the same governed page grouped by the view's registered axis.
     Lanes are observed DATA, not a status vocabulary in the host. Null or
     missing facts stay legible as Ungrouped instead of disappearing. */
  const boardItems: BoardItem[] = activeLens === 'board'
    ? data.map((row) => ({
        id: String(row.id),
        title: rowName(row),
        group: view.group_by == null || row[view.group_by] == null || row[view.group_by] === ''
          ? null : String(row[view.group_by]),
        details: view.columns.filter((column) => column !== view.group_by).slice(0, 3).map((column) => ({
          label: columnLabel(column),
          /* NEVER `String(row[column])`: for a reference column that is the raw
             UUID, which is exactly what #1993 exists to stop. A reference whose
             target is missing, deleted or not visible to this session stays an
             em dash; an ordinary column still shows its own value. */
          value: isReference(column)
            ? refLabel(column, row[column], '—', row)
            : String(row[column] ?? '—'),
        })),
      }))
    : []

  const itemsLabel = pluralize(sing).toLowerCase()

  /* Group by is OFFERED only where this list can state exact whole-result
     totals for that column (ADR F-84 §2) — an adapter that cannot never shows
     the item at all, rather than a disabled promise. */
  const groupingOffered = (column: string): boolean => !subject && groupDomainFor(column) != null
    && (serverSide ? Boolean(serverCollection.capabilities.grouping)
      : clientSide && Boolean(clientCollection.capabilities.grouping))
  const gridGrouping: GridGrouping | undefined = groupDomain
    && (effectiveGroupMode === 'server' || effectiveGroupMode === 'client')
    ? {
        column: groupDomain.column,
        columnLabel: columnLabel(groupDomain.column),
        keyOf: (row) => groupKeyOf(groupDomain, row[groupDomain.column]),
        labelOf: (key) => groupLabelOf(groupDomain, key),
        totalOf: (key) => (effectiveGroupMode === 'server'
          ? grouped.data.totals.get(key)
          : clientGrouped?.totals.get(key)) ?? null,
        continued: effectiveGroupMode === 'server' ? grouped.data.continued : undefined,
      }
    : undefined

  /* THE APPLIED FILTERS (#2375 §7) — one removable pill per filtered column,
     in the same words the column's editor uses; the pill reopens that editor.
     A leaf with no column (a registered composite) is still described, never
     dropped. A surface's own wording (`config.chipLabel`) wins, as it did. */
  const filterPills: GridFilterPill[] = []
  {
    const seen = new Set<string>()
    view.filters.forEach((leaf, index) => {
      const column = leaf.field
      if (column && seen.has(column)) return
      if (column) seen.add(column)
      const words = column && !config.chipLabel
        ? describeColumnFilter(filterFieldFor(column), view.filters as ColumnFilterLeaf[],
          (v) => refLabel(column, v, '') || undefined)
        : null
      filterPills.push({
        key: column ?? `filter-${index}`,
        column: column ? columnLabel(column) : 'Filter',
        condition: words?.condition ?? 'matches',
        value: words?.value ?? String(chipLabel(leaf) ?? ''),
        onRemove: () => mutate((v) => {
          v.filters = column ? v.filters.filter((f) => f.field !== column) : v.filters.filter((_, i) => i !== index)
        }),
        onOpen: column ? (anchor) => setColPanel({ kind: 'filter', column, anchor }) : undefined,
      })
    })
  }

  const listFooter = (() => {
    if (subjectRefusal) return null
    /* A server-GROUPED page pages FLATLY over the whole grouped result: its
       total is the sum of the whole-result group totals (ADR F-84 §2). */
    if (effectiveGroupMode === 'server') {
      return (
        <GridPager
          offset={offset}
          limit={PAGE_SIZE}
          loaded={data.length}
          total={grouped.data.total}
          itemsLabel={itemsLabel}
          busy={grouped.isFetching}
          onPageChange={(next) => setCursor(movePage(pagingKey, next))}
        />
      )
    }
    if (serverSide) {
      return (
        <GridPager
          offset={page.data.offset ?? offset}
          limit={page.data.limit ?? PAGE_SIZE}
          loaded={data.length}
          total={page.data.total}
          itemsLabel={itemsLabel}
          busy={page.isFetching}
          onPageChange={(next) => setCursor(movePage(pagingKey, next))}
        />
      )
    }

    // An untranslatable saved view is deliberately evaluated over the bounded
    // client pool. It cannot honestly offer a server pager, but it must still
    // say that the pool is capped.
    const capped = data.length >= PAGE_SIZE
    return (
      <div className={cx(lv.count, 'tnum')}>
        {data.length} {itemsLabel}
        {capped && (
          <span title={`Only the first ${PAGE_SIZE} rows can be evaluated for this view. Narrow its filters.`}>
            <Badge tone="warn">first {PAGE_SIZE}</Badge>
          </span>
        )}
      </div>
    )
  })()

  /* The governed create form, ready or not. It was a `(context) => ReactNode`
     render prop the grid called from inside a provisional row; it is an
     ordinary element now, and the host decides where it renders. */
  const quickCreateUnavailable = quickCreate && !governedCreateReady
    ? quickCreateForm.isError
      ? 'Quick create is unavailable because its governed form could not be resolved.'
      : quickCreateForm.isLoading
        ? 'Quick create is loading its governed form.'
        : 'Quick create is unavailable because no governed form applies.'
    : null

  /* The resolver's own diagnostics, which used to be three rows at the top of
     the hand-rolled switcher. `issues` carries the sentences
     `resolveTablePresentation` produced — an unsupported lens, an invalid
     document — and a fallback nobody can see is indistinguishable from a
     correct answer. #1982 acceptance #2 is the third: a saved view naming a
     lens this entity no longer offers is COUNTED with its reason, because a
     customer's saved work disappearing without explanation is worse than an
     unavailable option. */
  const switcherNotes = (
    <>
      {declared.status === 'fallback' && declared.issues.length > 0 && (
        <Menu.Label testId="presentation-diagnostic">{declared.issues.join(' · ')}</Menu.Label>
      )}
      {declared.error && (
        <Menu.Label testId="presentation-error">
          Presentation could not be resolved — showing the grid. {declared.error}
        </Menu.Label>
      )}
      {allViews.length > offeredViews.length && (
        <Menu.Label testId="views-unavailable">
          {allViews.length - offeredViews.length} view(s) hidden — this entity no longer offers their lens
        </Menu.Label>
      )}
    </>
  )

  /* THE CANVAS PATH chip is DELETED (#2375 §13, owner request: "that duplicate
     title needs to go, especially above the full title"). It rendered
     `LIST · <Entity> · <View>` above the page's own plural title — the view
     selector already names the view, and the page header names the collection
     once. No breadcrumb takes its place. */

  const body = (
    <>
      <WorkspaceSplit testId="split" inspectorPercent={split.pct}
        onInspectorPreview={split.preview} onInspectorCommit={split.commit} onInspectorReset={split.reset}
        inspectorId={peekDomId} inspectorLabel={`${sing} detail`}
        inspector={related
          ? <RelatedDetail entity={related.entity} id={related.id} onClose={() => setRelated(null)}
              onOpen={(e2, id2, n) => openRelated(e2, id2, n)} />
          : peekRow && config.peek && navigationEligible
            ? config.peek(peekRow, { orderedIds, navigate: (id) => onPeek(id), close: () => onPeek(undefined) })
            : undefined}
        primary={<>
        {/* THE GRID BAR (#UX). One component above every grid in the product:
            the Marble mark first — "before any title or any view selector" —
            then the view selector and the filters.

            The mark REPLACES `＋ New X`. Create is not lost: it opens the palette
            already scoped to this grid, where commands come first with
            `Create New` at the top, so ⌘K, ↓, ⏎ creates. One affordance per
            grid, holding the grid's whole vocabulary rather than the single verb
            that happened to fit in a toolbar.

            The COLUMN HEADERS are the bar's second row visually, but they stay
            in the DOM below inside `role="grid"` as a `role="row"`: lifting them
            into the bar would read as a grid with no header row to anyone using
            a screen reader, which is a worse trade than a shared parent. */}
      <CollectionWorkspace identity={`entity:${entity}`} testId="list-grid"
          commandLabel={`Command palette for ${pluralize(sing)}`}
          onCommand={() => openPalette(paletteScopeFor())}
          /* THE ONE BAR (#2375, ADR F-84): mark · commands|selection ·····
             search · Save as · view ▾ · ⋮. The governed New is the first
             command beside the mark when the command buttons are shown; from
             the first selected row the inline selection group replaces the
             commands in place. */
          commands={showCommands || config.readOnly ? (<>
            {showCommands && collectionCommands.bar}
            {config.readOnly && (
              <span className="supporting" data-testid="list-readonly">
                {config.readOnlyReason ?? `${sing} records are created through their own actions.`}
              </span>
            )}
          </>) : undefined}
          /* Batch actions are the governed `cardinality = 'batch'` acts first
             (ADR F-84 §1, F-54 §6); `config.batchActions` is TRANSITIONAL host
             compatibility (one surface) and follows them. */
          selection={{
            count: sel.size,
            label: `Actions for selected ${itemsLabel}`,
            onClear: () => setSel(new Set()),
            actions: (<>
              {actionSeam.renderBatch([...sel])}
              {(config.batchActions ?? []).map((a, i) => (
                <Button key={i} size="sm" variant={a.danger ? 'danger' : 'secondary'}
                  data-testid={`batch-action-${i}`}
                  onClick={(e) => { if (a.menu) setMenu({ kind: 'batch', anchor: e.currentTarget, batchIdx: i }); else { a.run?.([...sel]); setSel(new Set()) } }}>
                  {a.label}
                </Button>
              ))}
            </>),
          }}
          search={<GridSearch value={search} onChange={setSearch} collection={pluralize(sing)} unavailable={searchUnavailable} />}
          onSaveAs={draft ? () => void saveFork() : undefined}
          /* ONE control that names the current view and opens the list —
             Christopher, 2026-08-07: "view selectors are not rendered nicely".
             Since #2406 section 4 the widget itself is the kit's. Eligibility
             stays HERE: `offeredViews` is already filtered to the lenses this
             entity declares and the acting persona. */
          viewSelector={(
            <SavedViewSelector
              views={offeredViews.map((v) => ({ id: v.id, name: v.name, scope: v.scope as SavedViewScope }))}
              activeId={currentId ?? null}
              defaultId={resolvedId ?? null}
              modified={Boolean(draft)}
              personaLabel={persona}
              subject={sing.toLowerCase()}
              notes={switcherNotes}
              pinnedId={pinnedId}
              onPin={(id) => void pin.mutateAsync(id)}
              onSelect={(id) => { setActiveId(id); setDraft(null); setSel(new Set()) }} />
          )}
          /* THE VIEW SETTINGS ⋮ — replaces the Display and More buttons and the
             presentation switcher (#2375 §3.6). */
          viewSettings={(
            <ViewSettingsMenu subject={itemsLabel} modified={Boolean(draft)}
              commands={commandBarPreference === 'shown' ? collectionCommands.overflow : collectionCommands.all}
              sort={headersHidden ? {
                columns: view.columns.map((column) => ({ key: column, label: columnLabel(column) })),
                active: sortTerms[0] ?? null,
                onSort: (key, direction) => mutate((v) => { v.sort_spec = setSortTerm(v.sort_spec, key, direction, false) }),
              } : undefined}
              group={headersHidden ? {
                columns: view.columns.filter(groupingOffered).map((column) => ({ key: column, label: columnLabel(column) })),
                active: view.group_by ?? null,
                onGroup: (key) => mutate((v) => { v.group_by = key }),
              } : undefined}
              onColumns={headersHidden && view.columns.length > 0
                ? (anchor) => setColPanel({ kind: 'columns', column: view.columns[0]!, anchor })
                : undefined}
              onSaveAs={() => void saveFork()}
              onReset={() => setDraft(null)}
              onSetDefault={currentId && currentId !== '_default' ? () => void pin.mutateAsync(currentId) : undefined}
              presentation={{
                value: activeLens,
                options: declaredKinds
                  .filter((kind): kind is ViewKind => (HOST_LENSES as readonly string[]).includes(kind))
                  .map((kind) => ({ value: kind, label: LENS_SETTING_LABEL[kind] })),
                onChange: (value) => {
                  const kind = value as ViewKind
                  const compatible = offeredViews.find((candidate) => candidate.kind === kind)
                  if (compatible) { setActiveId(compatible.id); setDraft(null); return }
                  mutate((next) => { next.kind = kind })
                },
              }}
              wrap={{ checked: presentation.wrap, onChange: presentation.setWrap }}
              /* The filter route that survives the cards reflow (#2375): a phone
                 draws no column headers, so no column ⋮. Same editor, same pills. */
              filters={view.columns
                .filter((column) => filterFieldFor(column).allowedOps?.length !== 0)
                .map((column) => ({
                  key: column,
                  label: columnLabel(column),
                  active: view.filters.some((leaf) => leaf.field === column),
                  onFilter: (anchor: HTMLElement) => setColPanel({ kind: 'filter', column, anchor }),
                }))}
              commandButtons={{
                checked: commandBarPreference === 'shown',
                onChange: (next) => presentation.setCommandBar(next ? 'shown' : 'hidden'),
              }}
              onCopyLink={typeof navigator !== 'undefined' && navigator.clipboard
                ? () => { void navigator.clipboard.writeText(window.location.href) }
                : undefined}>
              <Menu.Label>Rows</Menu.Label>
              <Menu.RadioGroup value={density} onValueChange={(value) => setDensity(value as typeof density)}>
                {(['compact', 'default', 'comfortable'] as const).map((value) => (
                  <Menu.RadioItem key={value} value={value} testId={`density-${value}`}>
                    {value === 'default' ? 'Standard rows' : `${value[0]!.toUpperCase()}${value.slice(1)} rows`}
                  </Menu.RadioItem>
                ))}
              </Menu.RadioGroup>
              {config.mcpToolName && (
                <Menu.CheckItem checked={showMcp} onSelect={setShowMcp} testId="list-mcp">Show the MCP call</Menu.CheckItem>
              )}
              {authoringAuthority.data === true && (
                <Menu.Item testId="list-customize" onSelect={() => { setCustomizationFailure(null); setCustomizing(true) }}>
                  Customize table…
                </Menu.Item>
              )}
            </ViewSettingsMenu>
          )}
          filters={(
            <GridFilterPills pills={filterPills} onClearAll={() => mutate((v) => { v.filters = [] })} />
          )}
          notice={effectiveGroupMode === 'unsupported' && groupBy ? (
            <span data-testid="list-group-unsupported">
              This view groups by {columnLabel(groupBy)}, which this list cannot group
              exactly — {groupUnsupportedReason}. Shown ungrouped.
            </span>
          ) : undefined}
          footer={listFooter}
        >


        {/* The "Modified from … Save as my view / Reset" band is gone (#2375):
            a modified view says so on its selector (MODIFIED), "Save as…"
            sits beside it, and "Reset to saved" is in the view settings. A
            shared view still never mutates — Save as forks it. */}

        {showMcp && config.mcpToolName && (
          <div className={lv.mcp} data-testid="mcp">
            <span className={lv.mcpTool}>▸ {config.mcpToolName}</span>
            <code className={lv.mcpCode}>{JSON.stringify({
              entity, name: view.name, scope: view.scope,
              ...(view.persona_key ? { persona: view.persona_key } : {}),
              columns: view.columns, filters: { all: view.filters }, sort: view.sort_spec,
            })}</code>
          </div>
        )}

        {pickupError && (
          <div className={lv.modbar} role="alert" data-testid="pick-up-refusal">
            <span>{pickupError}</span><span className={lv.modSp} />
            <Button size="sm" variant="ghost" onClick={() => setPickupError(null)}>Dismiss</Button>
          </div>
        )}

        {/* DEGRADED, NOT DECORATIVE. This announced that the saved view could
            not be honoured, in `.hint` — muted supporting text, the treatment
            for a footnote. A reader was told the surface is not doing what they
            asked, in the styling the product uses for asides.

            `role="status"` stays and is NOT escalated: the grid below is real
            and usable, so this is degradation, not a block. An `alert` would
            interrupt a reader mid-task to tell them something already handled. */}
        {requestedLensDegraded && (
          <Notice tone="warn" live="status" data-testid="list-lens-degraded">
            The saved view presentation could not be resolved. Showing the safe grid instead.
          </Notice>
        )}

        {/* #2444 slice 4 — a SUBJECT entity's view, said honestly. A refused
            view shows no grid at all: an empty grid under it would read as
            "no records", which is exactly the lie a refusal must not tell. The
            filter chips above stay, so the person can take the condition off. */}
        {subjectRefusal && (
          <Notice tone="warn" live="status" data-testid="list-subject-refused">
            This view cannot be shown here. {pluralize(entLabel)} are read through a governed read that records
            every record it returns, and it cannot apply {subjectRefusal.reason}
            {subjectRefusal.field ? ` on ${String(fields.get(subjectRefusal.field)?.label ?? subjectRefusal.field)}` : ''}.
            Remove that filter to see the list.
          </Notice>
        )}
        {!subjectRefusal && subjectPlan && subjectPlan.sort.dropped.length > 0 && (
          <Notice tone="info" live="status" data-testid="list-subject-sort">
            Sorted by {String(fields.get(parseSortSpec(view.sort_spec)[0]?.key ?? '')?.label ?? parseSortSpec(view.sort_spec)[0]?.key ?? '')} only:
            {' '}{pluralize(entLabel).toLowerCase()} are read through a governed read that orders by one column.
          </Notice>
        )}

        {/* Board/calendar rows do not have the grid's action column. Keep the
            governed claim reachable without inventing a second write path: the
            first visible queue rows are offered here, and the grid view exposes
            every visible one. */}
        {activeLens !== 'grid' && queueRows.length > 0 && (
          <div className={lv.modbar} data-testid="queue-pickup-bar">
            <span><b>{queueRows.length}</b> queue item{queueRows.length === 1 ? '' : 's'} visible in this view</span>
            <span className={lv.modSp} />
            {queueRows.slice(0, 3).map((row) => (
              <Button key={String(row.id)} size="sm" variant="ghost"
                data-testid={`pick-up-${String(row.id)}`} disabled={pickup.isPending}
                onClick={() => { void pickUp(row) }}>
                Pick up {rowName(row)}
              </Button>
            ))}
            {queueRows.length > 3 && <span className="supporting">Switch to a grid view for all.</span>}
          </div>
        )}

        {/* THE LENS — the saved view's registered kind decides how the SAME
            governed rows are drawn. Both non-grid kinds are `@solum/ui`
            controls — `<Calendar>` and `<Board>`. No entity key, date column or
            status vocabulary is named in this host binding (#1000 / #1254). */}
        {subjectRefusal ? null : activeLens === 'calendar' ? (
          <section aria-label={`${view.name} calendar`} data-testid="list-calendar">
            <Calendar events={calendarEvents} onSelectEvent={navigationEligible ? (event) => openPeek(event.id) : undefined} />
          </section>
        ) : activeLens === 'board' ? (
          <section aria-label={`${view.name} board`} data-testid="list-board">
            <Board items={boardItems} onSelectItem={navigationEligible ? (item) => openPeek(item.id) : undefined} />
          </section>
        ) : (<>
        {/* THE GRID — `@solum/ui`'s <DataGrid> (#959).

            This block used to be ~160 lines of grid markup inline: the header
            row, the roving-tabindex data rows, the gridcells and their inline
            edit, the expansion row, the ghost create. It is the same markup, in
            the shared control kit, so `apps/portal` can render it too — the
            console's behaviour is unchanged because it is the same component,
            not a re-implementation of it.

            Everything below the seam is DATA the page resolved: the columns
            come from the saved view, the sort state from its `sort_spec`, the
            filtered flags from its leaves, the widths from the user's own
            settings, the expansion from the metamodel's registered slots, and
            the decoration from the entity's measures. */}
        {quickCreateUnavailable && <div role="status" data-testid="governed-quick-create-unavailable">
          {quickCreateUnavailable}
        </div>}
        {/* ── THE GOVERNED CREATE FORM (#2406 ruling 79217) ──────────────────
            "For an ordinary record collection, `New <record>` must open the
            canonical governed form directly in create mode", and the toolbar,
            the scoped Command Marble action and any keyboard equivalent invoke
            the SAME action — here, `setCreateOpen(true)`.

            A dialog rather than a row: nothing provisional appears before Save
            succeeds, so there is no placeholder identity in the collection and
            nothing to reconcile if the write is refused. Cancel closes and
            writes nothing; a refusal keeps the form and its values on screen,
            because `GovernedQuickCreate` renders the failure notice inside the
            form rather than closing over it.

            It is a `Modal`, not a second stacked one: an ordinary list page is
            not itself a modal, and the ruling's "do not stack a second modal"
            is about surfaces that already are. */}
        {createOpen && governedCreateReady && (
          <Modal
            label={`New ${sing.toLowerCase()}`}
            testid="governed-create"
            onClose={() => setCreateOpen(false)}
          >
            <GovernedQuickCreate
              entity={entity}
              schema={schema.data!}
              semantics={sem.data!}
              spec={quickCreate!}
              forms={[quickCreateForm.data!]}
              save={save}
              onClose={() => setCreateOpen(false)}
              onComplete={() => setCreateOpen(false)}
              onCreated={openPeek}
            />
          </Modal>
        )}
        <DataGrid
          onHeadersHiddenChange={setHeadersHidden}
          columns={gridColumns}
          rows={data}
          rowName={rowName}
          itemsLabel={pluralize(sing).toLowerCase()}
          /* The governed LENS is explicit. A selected grid never turns into
             cards merely because its container narrows; cards stay cards at
             every width. `compact` remains a legacy responsive fallback for
             DataGrid consumers that do not expose a presentation choice. */
          presentation={activeLens === 'cards' ? 'cards' : config.compact ? undefined : 'grid'}
          compact={activeLens === 'cards' ? undefined : config.compact}
          onActivateRow={navigationEligible ? openPeek : undefined}
          activeRowId={peekId}
          /* The row ↔ peek link (UX-06): a screen reader hears which element
             Enter opens. */
          rowControlsId={config.peek && navigationEligible ? peekDomId : undefined}
          /* THE LABEL IS THE SORT (#2375): ascending → descending → off; Shift
             adds the column as a further key. */
          onSortColumn={(c, options) => mutate((v) => {
            v.sort_spec = cycleSortTerm(v.sort_spec, c, options?.additive === true)
          })}
          grouping={gridGrouping}
          onMoveColumn={(c, toIndex) => mutate((v) => { v.columns = reorderColumns(v.columns, c, toIndex) })}
          wrapCells={presentation.wrap}
          onMenuOpenChange={setGridMenu}
          selection={sel}
          onSelectionChange={setSel}
          pinnedRowIds={presentation.pinnedRows}
          onPinnedRowIdsChange={presentation.setPinnedRows}
          pinnedColumns={presentation.pinnedColumns}
          widthOf={colWidths.widthOf}
          onResizeColumn={(c, px) => colWidths.set(c, px)}
          onResetColumn={(c) => colWidths.reset(c)}
          onCommitCell={inlineEditClosed ? undefined : commitCell}
          /* A twist is offered only where the metamodel says there is something
             behind it. An entity with no registered list slots keeps the list it
             always had — which is what makes bands safe to have on by default.

             A related row opens in the SPLIT, not as a new screen — but only
             where this list HAS a detail pane. A list with no `peek` has nowhere
             to put it, and the band falls back to the record page (the band's
             own default). */
          renderExpansion={canBand && navigationEligible
            ? (d) => (
              <RelationBand entity={entity} id={String(d.id)} label={rowName(d)}
                onOpenRecord={config.peek
                  ? (e2, id2, name) => { recordRecent(e2, id2); openRelated(e2, id2, name) }
                  : undefined} />
            )
            : undefined}
          /* AI Data Highlight (#10): measure-driven at-risk flags, with the
             date-overdue heuristic as the fallback. */
          decorateRow={(d) => {
            const flags = rowFlags(d, risk)
            const tone = flags.length ? 'warn' as const : rowTone(d, statusMachine.data ?? null)
            return {
              tone,
              flagCount: flags.length,
              title: flags.length ? flags.map((f) => f.label).join(' · ')
                : tone ? 'Overdue — past its due/deadline date' : undefined,
            }
          }}
          /* THE COLUMN'S OPTIONS (#2375 §6.3) — the kit's `GridColumnMenu`, the
             same `Menu` the view settings use. Sort, Filter…, Group by, Hide and
             Columns… live here now; the toolbar band that held Filter / Columns /
             Sort is gone. "Filter…" and "Columns…" open a second step anchored
             where the menu was. Group by is offered only where this list can
             state EXACT group totals (ADR F-84 §2). The display choices this menu
             used to carry (rows, wrap, command buttons, customize) moved to the
             view settings ⋮, where they are about the whole view. */
          renderColumnMenu={({ column, anchor, close, autosize }) => {
            const term = sortTerms.find((candidate) => candidate.key === column)
            const quick = config.columns[column]?.colMenuFilters ?? []
            return (
              <GridColumnMenu anchor={anchor} onClose={close} columnKey={column}
                label={columnLabel(column)}
                sort={{
                  direction: term?.direction ?? null,
                  onSort: (direction) => mutate((v) => { v.sort_spec = setSortTerm(v.sort_spec, column, direction, false) }),
                  onAddSort: sortTerms.length > 0 && !term
                    ? () => mutate((v) => { v.sort_spec = setSortTerm(v.sort_spec, column, 'asc', true) })
                    : undefined,
                }}
                onFilter={() => setColPanel({ kind: 'filter', column, anchor })}
                group={groupingOffered(column) ? {
                  active: view.group_by === column,
                  onToggle: () => mutate((v) => { v.group_by = v.group_by === column ? null : column }),
                } : undefined}
                /* Pin column left + Autosize (#2375 §6.3): the kit's one layout
                   implementation, over the person's pinned columns and widths. */
                pin={{
                  pinned: presentation.pinnedColumns.includes(column),
                  onToggle: () => presentation.setPinnedColumns(togglePinnedColumn(presentation.pinnedColumns, column)),
                }}
                onAutosize={autosize}
                onHide={view.columns.length > 1
                  ? () => mutate((v) => { v.columns = v.columns.filter((k) => k !== column) })
                  : undefined}
                onColumns={() => setColPanel({ kind: 'columns', column, anchor })}>
                {quick.length > 0 && (<>
                  {/* CURATED shortcuts, kept deliberately. "Overdue" and "Mine" are
                      not an operator and a value — they are a judgement about this
                      surface, and a generic operator UI cannot express one. */}
                  <Menu.Label>Quick filters</Menu.Label>
                  {quick.map((f, i) => (
                    <Menu.Item key={i} onSelect={() => { mutate(f.apply); close() }}>{f.label}</Menu.Item>
                  ))}
                  <Menu.Separator />
                </>)}
              </GridColumnMenu>
            )
          }}
          renderCellMenu={({ row, column, anchor, close, commit }) =>
            config.columns[column]?.menu?.(row, anchor, close, commit)}
        />
        </>
        )}
      </CollectionWorkspace>

      {/* The floating batch pill that stood here is DELETED (#2375): the batch
          acts are the inline selection group in the bar, from the first
          selected row. */}

      {/* The column ⋮ menu's SECOND step (#2375). The GENERIC editor is
          `@solum/ui`'s, so the customer portal gets the same one (#964). It
          knows the operators and the inputs; it does not know that a filter
          here means forking a shared saved view, which is what `mutate` is. */}
      {colPanel?.kind === 'filter' && (
        <AnchoredSurface anchor={colPanel.anchor} onClose={() => setColPanel(null)} width={280}>
          <AnchoredSurface.Section>Filter {columnLabel(colPanel.column)}</AnchoredSurface.Section>
          <ColumnFilterEditor
            field={filterFieldFor(colPanel.column)}
            filters={view.filters as ColumnFilterLeaf[]}
            // REPLACE this column's leaves, keep every other column's — see
            // `replaceColumnFilters`, which is where that property is tested.
            onApply={(leaves) => mutate((v) => {
              v.filters = replaceColumnFilters(v.filters, colPanel.column, leaves as Leaf[])
            })}
            onClear={() => mutate((v) => { v.filters = v.filters.filter((f) => f.field !== colPanel.column) })}
            onDone={() => setColPanel(null)}
            labelOfValue={(val) => refLabel(colPanel.column, val, '') || undefined}
            /* A `ref` column is a uuid, and nobody filters by typing one. The
               picker is the surface's own — the console already has one, and
               handing it in is what keeps the editor free of a data source. */
            renderRefValue={filterFieldFor(colPanel.column).refEntity
              ? ({ value, onChange, id }) => (
                <RefPicker id={id} value={value || null}
                  target={{
                    entity: filterFieldFor(colPanel.column).refEntity!,
                    label: columnLabel(colPanel.column),
                  }}
                  minChars={2} testid={`${id}-ref`}
                  onChange={(next) => onChange(next ?? '')} />
              )
              : undefined}
          />
        </AnchoredSurface>
      )}
      {/* "Columns…" — show, hide and add, in every column's menu, so a hidden
          column always has a way back. Order is the header's drag handle. */}
      {colPanel?.kind === 'columns' && (
        <AnchoredSurface anchor={colPanel.anchor} onClose={() => setColPanel(null)} width={250}>
          <AnchoredSurface.Section>Columns</AnchoredSurface.Section>
          {view.columns.map((k) => (
            <AnchoredSurface.Item key={k} data-testid={`columns-hide-${k}`}
              disabled={view.columns.length <= 1}
              onClick={() => { if (view.columns.length > 1) mutate((v) => { v.columns = v.columns.filter((c) => c !== k) }) }}>
              <AnchoredSurface.Check checked />{String(fields.get(k)?.label ?? k)}
            </AnchoredSurface.Item>
          ))}
          {(config.addableColumns ?? Object.keys(config.columns)).filter((k) => !view.columns.includes(k)).map((k) => (
            <AnchoredSurface.Item key={k} data-testid={`columns-add-${k}`}
              onClick={() => mutate((v) => { v.columns = [...v.columns, k] })}>
              <AnchoredSurface.Check checked={false} />{String(fields.get(k)?.label ?? k)}
            </AnchoredSurface.Item>
          ))}
        </AnchoredSurface>
      )}


      </>} />

      {menu?.kind === 'batch' && config.batchActions?.[menu.batchIdx]?.menu?.(menu.anchor, [...sel], () => { setSel(new Set()); closeMenu() })}
    </>
  )

  /**
   * The relationship SLOTS this entity registers, for per-relationship
   * authoring (#2276 owner correction, point 6).
   *
   * Its own component, mounted only while the editor is open, for two reasons
   * that are really one: a list that nobody is customizing should not be
   * resolving how its entity's relationships read, and a hook cannot be
   * conditional. Inline, it ran on every render of every list — which is a
   * round trip for an answer nothing on screen uses, and it reddened
   * fifty-nine list tests that legitimately mock the data layer and never
   * expected this door to be knocked on.
   *
   * `mapCapable` comes from the SAME governed door a record page asks.
   * `core.resolve_related_presentation` already answers it per slot from the
   * far entity's own document; resolving it a second way here would be a second
   * selector for a question the platform answers, and the two would disagree the
   * day a recipe moved. Asked with NO personal reading, because an author is
   * being shown what the deployment offers rather than their own choice.
   */
  const customizationQueries = [
    authoredPresentation, authoringCatalogues.personas, authoringCatalogues.forms,
  ]
  const authoringBody = authoringTarget && queryState(customizationQueries).kind === 'ready'
    && authoredPresentation.data
    ? (
      <>
      {customizationFailure && <WriteFailureNotice error={customizationFailure} what="this table presentation" />}
      <WithAuthoringSlots entity={entity}>{(slotChoices) => (
      <WithFormSections entity={entity}>{(formSections) => (
      <TablePresentationComposer
        key={tablePresentationTargetKey(authoringTarget)}
        value={authoredPresentation.data.document}
        targetKey={tablePresentationTargetKey(authoringTarget)}
        fields={(sem.data?.fields ?? []).map((field) => ({
          key: field.column_name, label: field.label,
          required: field.required, hasServerDefault: field.has_server_default,
        }))}
        forms={(authoringCatalogues.forms.data ?? []).map((form, index, catalogue) => ({
          key: form.logical_key, label: `${form.name} · ${form.scope}`,
          fields: form.sections.flatMap((section) => section.fields ?? []),
          automatic: index === catalogue.findIndex((candidate) => candidate.scope === 'system' || candidate.is_default),
        }))}
        actions={customizingHost === 'console' ? actionSeam.choices : []}
        commandActions={customizingHost === 'console' ? actionSeam.commandChoices : []}
        supported={authoringSupported}
        slots={slotChoices}
        sections={formSections}
        quickCreateAvailable={customizingHost === 'console'}
        cardActionsAvailable={customizingHost === 'console'}
        commandActionsAvailable={customizingHost === 'console'}
        layerLabel={customizingLayer === 'tenant'
          ? 'Tenant default'
          : authoringCatalogues.personas.data?.find((persona) => persona.key === targetPersona)?.label ?? targetPersona}
        provenance={authoredPresentation.data.provenance}
        issues={authoredPresentation.data.issues}
        busy={setPresentation.isPending || clearPresentation.isPending}
        layerControl={<div className={lv.tableManagerTarget}>
          <Select aria-label="Table presentation layer" value={customizingLayer}
            options={[{ value: 'tenant', label: 'Tenant default' }, { value: 'persona', label: 'Persona default' }]}
            onValueChange={(value) => setCustomizingLayer(value as 'tenant' | 'persona')} />
          {customizingLayer === 'persona' && <Select aria-label="Table presentation persona"
            value={targetPersona}
            options={(authoringCatalogues.personas.data ?? []).map((persona) => ({ value: persona.key, label: persona.label }))}
            onValueChange={setCustomizingPersona} />}
          <Select aria-label="Table presentation host" value={customizingHost}
            options={[{ value: 'console', label: 'Console' }, { value: 'portal', label: 'Customer portal' }]}
            onValueChange={(value) => setCustomizingHost(value as TablePresentationHost)} />
        </div>}
        onSave={(document) => setPresentation.mutate(
          { ...authoringTarget, document },
          tablePresentationWriteCallbacks(setCustomizationFailure, () => setCustomizing(false)),
        )}
        onCancel={() => { setCustomizationFailure(null); setCustomizing(false) }}
        onReset={targetOwnsTablePresentation(authoringTarget, authoredPresentation.data)
          ? () => clearPresentation.mutate(
              authoringTarget,
              tablePresentationWriteCallbacks(setCustomizationFailure, () => setCustomizing(false)),
            )
          : undefined}
      />
      )}</WithFormSections>
      )}</WithAuthoringSlots>
      </>
    ) : (
      <QueryBoundary queries={customizationQueries} label="the table presentation">
        {() => <Spinner />}
      </QueryBoundary>
    )
  const displayedBody = customizing ? authoringBody : body

  /* Christopher, 2026-08-07: "this is also messy at the top" — three stacked
     headers saying the same thing: the crumb card `LIST · Persons · All
     persons`, then an EYEBROW rendering the raw entity key (`CORE_PERSON`),
     then the title.

     The eyebrow goes: a machine key is not a heading, and the crumb card
     already names the type. The h1 STAYS — dropping it too left the page with
     no heading at all, which is an accessibility regression and what four
     browser specs were quite right to catch.

     #2014: when this list IS the routed destination it now says so with a
     `PageShell`, which is what carries the governed `--pad-page` inset. The
     crumb bar goes in the shell's eyebrow slot, which is the same position it
     already occupied — above the title — so nothing moves; what disappears is
     the second inset, because `CrumbBar.module.css` used to add `--sp-5` of its
     own on top of the page's `--sp-8` and the crumbs sat 20px right of the
     title they belong to.

     `chrome` is a REQUIRED prop rather than a default, because six of this
     component's twelve callers embed it inside a frame of their own and a
     default would have silently given half of them two page frames and, for
     Service and Subscriptions, a second h1. There is no right default; there is
     only a decision each caller has to state. */
  /* #2014 slice 3 — `embedded` is FRAMELESS. It used to draw `.page` and an h1
     of its own, which was right while its callers had no page frame and wrong
     the moment they gained one: a `PageShell` around a `.page` is the double
     inset this issue exists to remove, and a second `<h1>` under the shell's is
     a heading-order defect. Its six callers all own a `PageShell` now, so what
     is left here is the list's own context and its body. */
  /* #2375 §13: the plural title ONCE, and nothing above it — the canvas-path
     chip that used to sit in the eyebrow restated the collection and the view
     the page header and the view selector already name. */
  if (chrome === 'embedded') return <>{displayedBody}</>
  return (
    <PageShell width="full" testId="list-page" title={config.title ?? pluralize(entLabel)}>
      {displayedBody}
    </PageShell>
  )
}

/**
 * A RELATED record in the split pane — a different entity from the list's own.
 *
 * The list's `peek` renders one of ITS rows; a contact opened from an
 * organization's relation band is `core_person` while the list is
 * `core_organization`, so it cannot go through that seam. This renders the same
 * `RecordBody` the full page and the peek render — the third caller of the one
 * record body, not a third record UI.
 *
 * The header carries what the pane needs and the page cannot assume: the
 * entity's own name (you are looking at a Person inside an Organizations list,
 * and nothing else says so), a link to the full record, and a close.
 */
function RelatedDetail({ entity, id, onClose, onOpen }: {
  entity: string; id: string; onClose: () => void
  onOpen: (entity: string, id: string, name?: string) => void
}) {
  const schema = useSchema()
  const label = ((schema.data?.entities ?? []) as { key: string; label: string }[])
    .find((e) => e.key === entity)?.label ?? entity
  return (<>
    <PeekHeader lead={singularize(label)} onClose={onClose}
      actions={(
        <Link to="/e/$entity/$id" params={{ entity, id }} aria-label="Open the full record"
          title="Open the full record"
          onClick={() => setTrail(`/e/${entity}/${id}`, [{ label, to: `/e/${entity}` }])}>⤢</Link>
      )} />
    <PeekBody>
      {/* The title is the RECORD BODY's now, so the mark beside it is on every
          surface by construction — this pane drew its own h2, which is exactly
          how a button lands on the page and not here. */}
      <RecordBody entity={entity} id={id} presentation="pane" relations
        headingTestid="related-heading"
        onSaved={({ intent }) => { if (intent === 'close') onClose() }}
        onOpenRelated={(e2, id2, n) => onOpen(e2, id2, n)} />
    </PeekBody>
  </>)
}

/**
 * Resolve this entity's registered relationship slots for per-relationship
 * authoring, and hand them to whatever is being drawn (#2276 owner correction).
 *
 * A component rather than a hook call in `ListView` because it must only run
 * while the editor is open: a list nobody is customizing should not be asking
 * how its entity's relationships read, and a hook cannot be conditional.
 */
function WithAuthoringSlots({ entity, children }: {
  entity: string
  children: (slots: readonly TableSlotChoice[]) => ReactNode
}) {
  const schema = useSchema()
  const slots = useMemo(() => listSlots(schema.data, entity), [schema.data, entity])
  const refs = useMemo(
    () => slots.map((slot) => ({ slot: slot.id, target: slot.otherEntity })), [slots])
  // A stable empty object: the author is shown what the DEPLOYMENT offers, and
  // an inline `{}` would be a new query key on every render.
  const none = useMemo(() => ({}), [])
  const related = useRelatedPresentation(entity, refs, none)
  const choices = useMemo(() => slots.map((slot) => ({
    id: slot.id,
    label: slot.label,
    target: slot.otherEntity,
    targetLabel: slot.otherLabel,
    mapCapable: related.of(slot.id).mapCapable,
    /* Asked SEPARATELY from map, because they are different questions of the
       same target: map needs a coordinate recipe, peek needs a canonical
       readable form. One combined flag would make "coordinates but no form"
       unrepresentable, and that is an ordinary entity. */
    peekCapable: related.of(slot.id).peekCapable,
  })), [slots, related])
  return <>{children(choices)}</>
}

/**
 * THIS entity's own form sections, by declared id — what a Peek recipe selects
 * from (#2281).
 *
 * Its own component for the same reason `WithAuthoringSlots` is: a list nobody
 * is customizing should not be resolving its entity's form, and a hook cannot be
 * conditional.
 */
function WithFormSections({ entity, children }: {
  entity: string
  children: (sections: readonly { id: string; label: string }[]) => ReactNode
}) {
  const schema = useSchema()
  const sem = useSemantics(entity)
  const canonical = useCanonicalForm(entity)
  const sections = useMemo(() => {
    if (!schema.data || !sem.data) return []
    const document = canonical.data && typeof canonical.data === 'object'
      && Array.isArray((canonical.data as { sections?: unknown }).sections)
      ? canonical.data as Parameters<typeof buildForm>[3]
      : undefined
    // The SAME resolution the record page and the preview use, so the ids an
    // author picks are the ids the renderer will look for.
    return formLayout(buildForm(schema.data, sem.data, entity, document))
      .document.sections.map((section) => ({ id: section.id, label: section.label }))
  }, [schema.data, sem.data, canonical.data, entity])
  return <>{children(sections)}</>
}
